import * as THREE from "three";
import { Client, Room } from "colyseus.js";
import "./styles.css";

type CropType = "wheat" | "carrot" | "pumpkin" | "apple" | "egg" | "milk" | "wool";
type CharacterId = "farmer" | "degen" | "builder" | "trader";
type CropRowDef = { type: CropType; z: number; x0: number; count: number; spacing?: number };
type BonusType = "coin" | "boost" | "crate";
type BonusState = { id: string; type: BonusType; x: number; z: number; ready: boolean; value: number };
type WorldBlocker = { x: number; z: number; radius: number; height: number };
type DemoNpc = { rig: PlayerRig; path: THREE.Vector3[]; pathIndex: number; speed: number; jumpTimer: number; taskTimer: number; effectTimer: number; name: string };
type AudioKit = { context: AudioContext; master: GainNode; music: GainNode; sfx: GainNode; ambience: GainNode; musicTimer?: number; ambienceSource?: AudioBufferSourceNode };
type SignHandle = { group: THREE.Group; face: THREE.Mesh };

const WORLD_SIZE = 620;
const WORLD_LIMIT = 288;
const PLAYER_HEIGHT = 0;
const GRAVITY = 30;
const JUMP_SPEED = 16.4;
const WALK_SPEED = 16;
const SPRINT_SPEED = 27;
const PLAYER_RADIUS = 1.25;
const REWARD_ROUND_SECONDS = 300;
const TOKEN_REFRESH_MS = 90_000;
const PLAYER_POOL_BPS = 7000;
const DEVELOPMENT_RESERVE_BPS = 3000;
const CROP_INTERACT_DISTANCE_SQ = 72;
const BONUS_INTERACT_DISTANCE_SQ = 34;
const TRENCHER_NAMES = [
  "MarsMax", "DomeDan", "OxyMira", "RoverRay", "DustDegen", "CraterCat", "HabHank", "RocketRina",
  "IceVic", "SolarSage", "RedWick", "OrbitOllie", "BagBenny", "MoonMira", "YieldYuri", "SniperSam",
  "VaultVega", "MemeMara", "HolderHal", "LaunchLex", "AlphaAsh", "CommsKai", "PumpPablo", "CropCaller",
  "DomeLord", "RedChad", "OxyApe", "MarsMfer", "PumpPilot", "IceSniper", "CraterKing", "SolarSzn",
];

const CHARACTER_STYLES: Record<CharacterId, { label: string; color: string; hat: string; accent: string; accessory: "rake" | "candle" | "hammer" | "tablet" }> = {
  farmer: { label: "Terraformer", color: "#2ba85d", hat: "white", accent: "#6bd7ff", accessory: "rake" },
  degen: { label: "Mars Degen", color: "#4b1f17", hat: "green", accent: "#f0c542", accessory: "candle" },
  builder: { label: "Hab Builder", color: "#d66a36", hat: "yellow", accent: "#3b3025", accessory: "hammer" },
  trader: { label: "Orbit Trader", color: "#2f67b1", hat: "black", accent: "#6bd7ff", accessory: "tablet" },
};

const CROP_LAYOUT: CropRowDef[] = [
  { type: "wheat", z: -184, x0: -238, count: 17 },
  { type: "wheat", z: -168, x0: -238, count: 17 },
  { type: "carrot", z: -152, x0: -238, count: 17 },
  { type: "carrot", z: -136, x0: -238, count: 17 },
  { type: "pumpkin", z: -118, x0: -238, count: 15 },
  { type: "wool", z: -98, x0: -236, count: 15 },

  { type: "carrot", z: -192, x0: -96, count: 17 },
  { type: "carrot", z: -176, x0: -96, count: 17 },
  { type: "pumpkin", z: -160, x0: -96, count: 17 },
  { type: "pumpkin", z: -144, x0: -96, count: 17 },
  { type: "wheat", z: -126, x0: -96, count: 15 },

  { type: "pumpkin", z: -190, x0: 58, count: 20 },
  { type: "pumpkin", z: -174, x0: 58, count: 20 },
  { type: "wheat", z: -158, x0: 58, count: 20 },
  { type: "wheat", z: -142, x0: 58, count: 20 },
  { type: "apple", z: -116, x0: 92, count: 14 },
  { type: "apple", z: -100, x0: 92, count: 14 },

  { type: "wheat", z: -58, x0: -252, count: 16 },
  { type: "egg", z: -42, x0: -252, count: 16 },
  { type: "egg", z: -22, x0: -252, count: 16 },
  { type: "milk", z: -6, x0: -252, count: 16 },

  { type: "wheat", z: -54, x0: -72, count: 9 },
  { type: "carrot", z: -38, x0: -72, count: 9 },
  { type: "pumpkin", z: -54, x0: 10, count: 9 },
  { type: "apple", z: -38, x0: 10, count: 9 },
  { type: "egg", z: 26, x0: -84, count: 8 },
  { type: "milk", z: 42, x0: -84, count: 8 },
  { type: "wool", z: 32, x0: 36, count: 8 },
  { type: "carrot", z: 48, x0: 36, count: 8 },

  { type: "wheat", z: 74, x0: -54, count: 12 },
  { type: "carrot", z: 90, x0: -54, count: 12 },
  { type: "pumpkin", z: 106, x0: -54, count: 12 },
  { type: "milk", z: 84, x0: 18, count: 12 },
  { type: "wool", z: 100, x0: 18, count: 12 },

  { type: "apple", z: 30, x0: 154, count: 15 },
  { type: "apple", z: 46, x0: 154, count: 15 },
  { type: "apple", z: 62, x0: 154, count: 15 },
  { type: "wool", z: 84, x0: 162, count: 14 },
  { type: "wool", z: 100, x0: 162, count: 14 },
  { type: "milk", z: 118, x0: 162, count: 14 },

  { type: "egg", z: 106, x0: -244, count: 16 },
  { type: "egg", z: 122, x0: -244, count: 16 },
  { type: "wheat", z: 138, x0: -244, count: 16 },
  { type: "wheat", z: 154, x0: -244, count: 16 },
  { type: "pumpkin", z: 172, x0: -244, count: 16 },

  { type: "milk", z: 138, x0: -116, count: 16 },
  { type: "milk", z: 154, x0: -116, count: 16 },
  { type: "wool", z: 170, x0: -116, count: 16 },
  { type: "wool", z: 186, x0: -116, count: 16 },

  { type: "milk", z: 138, x0: 34, count: 17 },
  { type: "carrot", z: 154, x0: 34, count: 17 },
  { type: "carrot", z: 170, x0: 34, count: 17 },
  { type: "pumpkin", z: 186, x0: 34, count: 17 },

  { type: "carrot", z: 138, x0: 152, count: 15 },
  { type: "wheat", z: 154, x0: 152, count: 15 },
  { type: "pumpkin", z: 170, x0: 152, count: 15 },
  { type: "pumpkin", z: 186, x0: 152, count: 15 },

  { type: "egg", z: -246, x0: -250, count: 18 },
  { type: "milk", z: -232, x0: -250, count: 18 },
  { type: "wool", z: -246, x0: -108, count: 17 },
  { type: "apple", z: -232, x0: -108, count: 17 },
  { type: "carrot", z: -246, x0: 42, count: 18 },
  { type: "wheat", z: -232, x0: 42, count: 18 },
  { type: "pumpkin", z: -246, x0: 182, count: 14 },
  { type: "egg", z: -232, x0: 182, count: 14 },

  { type: "milk", z: 224, x0: -248, count: 18 },
  { type: "wool", z: 240, x0: -248, count: 18 },
  { type: "apple", z: 224, x0: -102, count: 18 },
  { type: "carrot", z: 240, x0: -102, count: 18 },
  { type: "wheat", z: 224, x0: 52, count: 18 },
  { type: "pumpkin", z: 240, x0: 52, count: 18 },
  { type: "egg", z: 224, x0: 194, count: 14 },
  { type: "milk", z: 240, x0: 194, count: 14 },
];

interface PlayerState {
  id: string;
  name: string;
  x: number;
  y: number;
  z: number;
  rot: number;
  color: string;
  hat: string;
  harvested: number;
  cash: number;
  wallet: string;
  holder: boolean;
}

interface CropState {
  id: string;
  type: CropType;
  x: number;
  z: number;
  ready: boolean;
}

interface OrderState {
  id: string;
  customer: string;
  reward: number;
  wants: Partial<Record<CropType, number>>;
}

interface ChatRow {
  id: string;
  author: string;
  message: string;
  createdAt: string;
}

interface LeaderRow {
  id: string;
  name: string;
  cash: number;
  harvested: number;
  wallet: string;
  holder: boolean;
}

interface WorldInit {
  id: string;
  players: PlayerState[];
  crops: CropState[];
  orders: OrderState[];
  chat: ChatRow[];
  inventory: Record<CropType, number>;
  leaderboard: LeaderRow[];
}

interface TokenSummary {
  ok: boolean;
  mint: string;
  fetchedAt: string;
  source: string;
  holderRowsSeen: number;
  eligibleHolders: number;
  totalQuantity: number;
  recentTrades: number;
  recentVolumeUsd: number;
  topHolders: Array<{ rank: number; account: string; accountShort: string; quantity: number; ratio: number; isLiqPool: boolean }>;
  rewardEngine: {
    mode: string;
    pumpSdk: string;
    realClaims: boolean;
    realPayouts: boolean;
    payoutAsset: string;
  };
  notes: string[];
}

interface PlayerRig {
  group: THREE.Group;
  label: THREE.Sprite;
  target: THREE.Vector3;
  rot: number;
}

const PUBLIC_GAME_WS_URL = "wss://chat.futurechad.xyz";
const PUBLIC_GAME_API_URL = "https://chat.futurechad.xyz";
const DEFAULT_TOKEN_MINT = "9DYuDYACC2CEj3QrXTLqurks2PExDEcAvy2sJBEnpump";
const TOKEN_MINT = envValue("VITE_TOKEN_MINT") || DEFAULT_TOKEN_MINT;
const SIMULATOR_ENABLED = new URLSearchParams(location.search).get("sim") === "1";

const canvas = qs<HTMLCanvasElement>("#game");
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;

const scene = new THREE.Scene();
scene.background = new THREE.Color("#170d1f");
scene.fog = new THREE.Fog("#9e4a2f", 210, 560);

const camera = new THREE.PerspectiveCamera(58, 1, 0.1, 560);
const clock = new THREE.Clock();
const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();
const savedName = localStorage.getItem("marsFarmName");
let localName = savedName && !savedName.startsWith("farmer-") && !/-\d+$/.test(savedName) ? savedName : randomTrencherName();
if (!savedName || savedName.startsWith("farmer-") || /-\d+$/.test(savedName)) localStorage.setItem("marsFarmName", localName);

let room: Room | undefined;
let localId = "";
let localPlayer: PlayerState | undefined;
let crops: CropState[] = [];
let orders: OrderState[] = [];
let inventory: Record<CropType, number> = emptyInventory();
let chatRows: ChatRow[] = [];
let leaders: LeaderRow[] = [];
let yaw = -0.15;
let pitch = 0.28;
let lookDragging = false;
let activeCrop: CropState | undefined;
let activeBonus: BonusState | undefined;
let nearOrderBoard = false;
let lastSent = 0;
let offlineMode = false;
let worldLoaded = false;
let gameEntered = false;
let verticalVelocity = 0;
let grounded = true;
let wasGrounded = true;
let stepCooldown = 0;
let audioKit: AudioKit | undefined;
let audioEnabled = localStorage.getItem("marsFarmAudio") !== "off";
let holderWallet = localStorage.getItem("marsFarmHolderWallet") || "";
let payoutAddress = localStorage.getItem("marsFarmPayout") || holderWallet;
let rewardRoundEndsAt = Date.now() + REWARD_ROUND_SECONDS * 1000;
let lastRewardTimerValue = "";
let latestTokenSummary: TokenSummary | undefined;
let holderSyncSent = false;

const keys = new Set<string>();
const mobileMove = new THREE.Vector2();
const localVelocity = new THREE.Vector3();
let selectedCharacter = normalizeCharacter(localStorage.getItem("marsFarmCharacter"));
const localRig = createPlayerRig({
  id: "local",
  name: localName,
  x: -8,
  y: 0,
  z: 6,
  rot: 0,
  color: CHARACTER_STYLES[selectedCharacter].color,
  hat: CHARACTER_STYLES[selectedCharacter].hat,
  harvested: 0,
  cash: 0,
  wallet: "",
  holder: false,
});
scene.add(localRig.group);

const remoteRigs = new Map<string, PlayerRig>();
const cropMeshes = new Map<string, THREE.Group>();
const cropPickTargets: THREE.Object3D[] = [];
const bonusMeshes = new Map<string, THREE.Group>();
const bonuses: BonusState[] = [];
const worldBlockers: WorldBlocker[] = [];
const demoNpcs: DemoNpc[] = [];
const yieldPrompts: THREE.Sprite[] = [];
const popupTexts: Array<{ sprite: THREE.Sprite; life: number; velocity: THREE.Vector3 }> = [];
const harvestParticles: Array<{ mesh: THREE.Mesh; life: number; velocity: THREE.Vector3 }> = [];
let interactionPrompt: THREE.Sprite;

const ui = {
  connection: qs("#connection"),
  harvested: qs("#harvested"),
  cash: qs("#cash"),
  online: qs("#online"),
  yieldList: qs("#yieldList"),
  leaderboard: qs("#leaderboard"),
  orders: qs("#orders"),
  inventory: qs("#inventory"),
  chatLog: qs("#chatLog"),
  chatForm: qs<HTMLFormElement>("#chatForm"),
  questFill: qs("#questFill"),
  questTitle: qs("#questTitle"),
  questText: qs("#questText"),
  actionHint: qs("#actionHint"),
  toast: qs("#toast"),
  soundToggle: qs<HTMLButtonElement>("#soundToggle"),
  connectWallet: qs<HTMLButtonElement>("#connectWallet"),
  walletLabel: qs("#walletLabel"),
  setPayout: qs<HTMLButtonElement>("#setPayout"),
  rewardPot: qs("#rewardPot"),
  rewardShare: qs("#rewardShare"),
  rewardReserve: qs("#rewardReserve"),
  rewardTimer: qs("#rewardTimer"),
  rewardRows: qs("#rewardRows"),
  holderNotice: qs("#holderNotice"),
  tokenMint: qs("#tokenMint"),
  tokenStats: qs("#tokenStats"),
  holderRows: qs("#holderRows"),
  playerName: qs<HTMLInputElement>("#playerName"),
  randomName: qs<HTMLButtonElement>("#randomName"),
  characterSelect: qs<HTMLElement>("#characterSelect"),
  enterWorld: qs<HTMLButtonElement>("#enterWorld"),
  mobileControls: qs<HTMLElement>("#mobileControls"),
  verifyModal: qs<HTMLElement>("#verifyModal"),
  verifyClose: qs<HTMLButtonElement>("#verifyClose"),
  verifyWalletInput: qs<HTMLInputElement>("#verifyWalletInput"),
  verifyPayoutInput: qs<HTMLInputElement>("#verifyPayoutInput"),
  verifySubmit: qs<HTMLButtonElement>("#verifySubmit"),
};

initScene();
wireInput();
connect();
refreshTokenSummary();
window.setInterval(refreshTokenSummary, TOKEN_REFRESH_MS);
animate();

function initScene(): void {
  const hemi = new THREE.HemisphereLight("#ffd6a7", "#552418", 1.9);
  scene.add(hemi);

  const sun = new THREE.DirectionalLight("#ffd7a0", 3.35);
  sun.position.set(-90, 116, 44);
  sun.castShadow = true;
  sun.shadow.mapSize.set(3072, 3072);
  sun.shadow.camera.near = 1;
  sun.shadow.camera.far = 380;
  sun.shadow.camera.left = -210;
  sun.shadow.camera.right = 210;
  sun.shadow.camera.top = 210;
  sun.shadow.camera.bottom = -210;
  scene.add(sun);

  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(WORLD_SIZE, WORLD_SIZE, 28, 28),
    new THREE.MeshLambertMaterial({ color: "#b95a37" }),
  );
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  scene.add(ground);

  addSkyBand();
  addMarsCraters();
  addFarmPlots();
  addBuildings();
  addDecor();
  addBonuses();
  if (SIMULATOR_ENABLED) addDemoNpcs();
  interactionPrompt = makeTextSprite("Press E", 220, 64, "#f7f1dc", "#138b48", 30);
  interactionPrompt.scale.set(4.6, 1.25, 1);
  interactionPrompt.visible = false;
  scene.add(interactionPrompt);
}

function addSkyBand(): void {
  const horizon = new THREE.Mesh(
    new THREE.CylinderGeometry(380, 380, 78, 64, 1, true),
    new THREE.MeshBasicMaterial({ color: "#170d1f", side: THREE.BackSide }),
  );
  horizon.position.y = 32;
  scene.add(horizon);

  const curve = new THREE.Mesh(
    new THREE.SphereGeometry(370, 48, 18),
    new THREE.MeshLambertMaterial({ color: "#9f4b30" }),
  );
  curve.position.set(0, -366, 0);
  curve.scale.y = 0.38;
  scene.add(curve);

  const starGeometry = new THREE.BufferGeometry();
  const starPositions: number[] = [];
  for (let i = 0; i < 520; i += 1) {
    const angle = seeded(i * 17.7, 0, Math.PI * 2);
    const radius = seeded(i * 13.1, 230, 345);
    starPositions.push(Math.cos(angle) * radius, seeded(i * 5.3, 42, 180), Math.sin(angle) * radius);
  }
  starGeometry.setAttribute("position", new THREE.Float32BufferAttribute(starPositions, 3));
  const stars = new THREE.Points(starGeometry, new THREE.PointsMaterial({ color: "#fff3d5", size: 1.25, sizeAttenuation: true }));
  scene.add(stars);

  const earth = new THREE.Mesh(new THREE.SphereGeometry(10, 18, 12), new THREE.MeshBasicMaterial({ color: "#4c8cff" }));
  earth.position.set(92, 136, -252);
  scene.add(earth);
  const earthGlow = new THREE.Mesh(new THREE.SphereGeometry(10.8, 18, 12), new THREE.MeshBasicMaterial({ color: "#b9f5ff", transparent: true, opacity: 0.22 }));
  earthGlow.position.copy(earth.position);
  scene.add(earthGlow);

  const moon = new THREE.Mesh(new THREE.SphereGeometry(3.1, 12, 8), new THREE.MeshBasicMaterial({ color: "#d8d1c3" }));
  moon.position.set(112, 145, -246);
  scene.add(moon);

  const sunDisc = new THREE.Mesh(new THREE.SphereGeometry(15, 20, 12), new THREE.MeshBasicMaterial({ color: "#ffd36b" }));
  sunDisc.position.set(-198, 152, -216);
  scene.add(sunDisc);
  const sunGlow = new THREE.Mesh(new THREE.SphereGeometry(21, 20, 12), new THREE.MeshBasicMaterial({ color: "#ff9b54", transparent: true, opacity: 0.24 }));
  sunGlow.position.copy(sunDisc.position);
  scene.add(sunGlow);
}

function addMarsCraters(): void {
  const craterMat = new THREE.MeshLambertMaterial({ color: "#7f3928" });
  const rimMat = new THREE.MeshLambertMaterial({ color: "#d17a4d" });
  const spots: Array<[number, number, number]> = [
    [-268, -246, 19], [-210, 226, 15], [-146, -236, 12], [-58, 226, 10], [68, -244, 13],
    [186, 226, 17], [258, -218, 14], [-292, -22, 13], [292, -12, 15], [248, 202, 11],
    [-36, -96, 8], [148, -126, 10], [-180, 128, 9], [232, 70, 8], [-238, 70, 7],
  ];
  spots.forEach(([x, z, radius], index) => {
    const crater = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius * 1.08, 0.13, 18), craterMat);
    crater.position.set(x, 0.08, z);
    crater.scale.y = 0.12;
    crater.receiveShadow = true;
    scene.add(crater);

    const rim = new THREE.Mesh(new THREE.TorusGeometry(radius * 1.04, Math.max(0.32, radius * 0.055), 6, 22), rimMat);
    rim.position.set(x, 0.22, z);
    rim.rotation.x = Math.PI / 2;
    rim.rotation.z = seeded(index * 3.7, -0.4, 0.4);
    rim.scale.set(1, seeded(index, 0.68, 0.96), 1);
    rim.castShadow = true;
    scene.add(rim);
  });
}

function addFarmPlots(): void {
  const plotMaterial = new THREE.MeshLambertMaterial({ color: "#743522" });
  const boardMaterial = new THREE.MeshLambertMaterial({ color: "#4a2318" });
  const roadMaterial = new THREE.MeshLambertMaterial({ color: "#d18955" });
  const plazaMaterial = new THREE.MeshLambertMaterial({ color: "#d7a06b" });

  addRoad(0, 0, 520, 14, roadMaterial);
  addRoad(0, 0, 14, 510, roadMaterial);
  addRoad(-112, -98, 190, 12, roadMaterial);
  addRoad(128, -104, 170, 12, roadMaterial);
  addRoad(-132, 120, 240, 12, roadMaterial);
  addRoad(154, 104, 190, 12, roadMaterial);
  addRoad(-194, 0, 12, 220, roadMaterial);
  addRoad(206, 0, 12, 230, roadMaterial);
  addRoad(-166, -214, 160, 9, roadMaterial);
  addRoad(126, -214, 214, 9, roadMaterial);
  addRoad(-172, 218, 176, 9, roadMaterial);
  addRoad(136, 218, 214, 9, roadMaterial);
  addRoad(-284, 46, 9, 206, roadMaterial);
  addRoad(284, 44, 9, 220, roadMaterial);
  addRoad(-18, -224, 9, 96, roadMaterial);
  addRoad(18, 218, 9, 132, roadMaterial);

  const plaza = new THREE.Mesh(new THREE.CylinderGeometry(30, 33, 0.34, 8), plazaMaterial);
  plaza.position.set(0, 0.18, 0);
  plaza.rotation.y = Math.PI / 8;
  plaza.receiveShadow = true;
  plaza.castShadow = true;
  scene.add(plaza);
  addLandingPad(0, 0);
  addWoodSign("SPAWN DOME", -8, -30, 0);
  addWoodSign("MISSION HUB", 92, -18, -0.5);
  addWoodSign("HAB BAY", -104, -16, 0.45);

  CROP_LAYOUT.forEach((row, index) => {
    const spacing = row.spacing || 5.5;
    const width = row.count * spacing + 8;
    addPlot(row.x0 - 4, row.z, width, 12, plotMaterial, boardMaterial, index % 4);
    if (index % 2 === 0 || row.count >= 14) addCropRail(row.x0 - 4, row.z, width, 12, index % 3);
  });
}

function addRoad(x: number, z: number, w: number, d: number, material: THREE.Material): void {
  const road = new THREE.Mesh(new THREE.BoxGeometry(w, 0.16, d), material);
  road.position.set(x, 0.1, z);
  road.receiveShadow = true;
  scene.add(road);
}

function addLandingPad(x: number, z: number): void {
  const group = new THREE.Group();
  const pad = new THREE.Mesh(new THREE.CylinderGeometry(18, 20, 0.28, 16), new THREE.MeshLambertMaterial({ color: "#4f3b34" }));
  pad.position.y = 0.36;
  pad.castShadow = true;
  pad.receiveShadow = true;
  group.add(pad);

  const ring = new THREE.Mesh(new THREE.TorusGeometry(14.4, 0.34, 6, 36), new THREE.MeshLambertMaterial({ color: "#6bd7ff" }));
  ring.position.y = 0.62;
  ring.rotation.x = Math.PI / 2;
  group.add(ring);

  const inner = new THREE.Mesh(new THREE.TorusGeometry(7.4, 0.24, 6, 30), new THREE.MeshLambertMaterial({ color: "#f0c542" }));
  inner.position.y = 0.66;
  inner.rotation.x = Math.PI / 2;
  group.add(inner);

  for (let i = 0; i < 6; i += 1) {
    const angle = (i / 6) * Math.PI * 2;
    const beacon = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.38, 2.1, 8), new THREE.MeshLambertMaterial({ color: "#2f67b1" }));
    beacon.position.set(Math.cos(angle) * 16.6, 1.35, Math.sin(angle) * 16.6);
    beacon.castShadow = true;
    group.add(beacon);
    const glow = new THREE.Mesh(new THREE.SphereGeometry(0.42, 8, 6), new THREE.MeshLambertMaterial({ color: "#57d96f" }));
    glow.position.set(Math.cos(angle) * 16.6, 2.55, Math.sin(angle) * 16.6);
    group.add(glow);
  }

  const marker = makeMountedSign("MARS LANDING", 260, 58, "#fff7df", "#4b251a", 26, 8.4, 1.7);
  marker.group.position.set(0, 3.45, 17.2);
  group.add(marker.group);
  group.position.set(x, 0, z);
  scene.add(group);
}

function addPlot(x: number, z: number, w: number, d: number, plotMaterial: THREE.Material, boardMaterial: THREE.Material, state: number): void {
  const plot = new THREE.Mesh(new THREE.BoxGeometry(w, 0.38, d), plotMaterial);
  plot.position.set(x + w / 2, 0.18, z);
  plot.receiveShadow = true;
  plot.castShadow = true;
  scene.add(plot);
  for (let i = 0; i < Math.max(3, Math.floor(d / 2.6)); i += 1) {
    const board = new THREE.Mesh(new THREE.BoxGeometry(w - 2, 0.08, 0.28), boardMaterial);
    board.position.set(x + w / 2, 0.43, z - d / 2 + 1.6 + i * 2.2);
    board.castShadow = true;
    scene.add(board);
  }
  if (state === 0) return;
  const markerMat = new THREE.MeshLambertMaterial({ color: state === 1 ? "#7b5d30" : state === 2 ? "#74a344" : "#df8a28" });
  for (let i = 0; i < Math.floor(w / 9); i += 1) {
    const marker = state === 3
      ? new THREE.Mesh(new THREE.SphereGeometry(0.9, 8, 6), markerMat)
      : new THREE.Mesh(new THREE.ConeGeometry(0.45, state === 2 ? 1.8 : 0.7, 6), markerMat);
    marker.position.set(x + 5 + i * 8.5, state === 3 ? 0.95 : 0.75, z + Math.sin(i) * (d / 3));
    marker.castShadow = true;
    scene.add(marker);
  }
}

function addCropRail(x: number, z: number, w: number, d: number, variant = 0): void {
  const group = new THREE.Group();
  const postMat = new THREE.MeshLambertMaterial({ color: variant === 1 ? "#6b3a24" : "#7b4a22" });
  const railMat = new THREE.MeshLambertMaterial({ color: variant === 2 ? "#f0c542" : "#d7a447" });
  const cx = x + w / 2;
  const postGeo = new THREE.BoxGeometry(0.42, 1.1, 0.42);
  const railLongGeo = new THREE.BoxGeometry(Math.max(5, w - 4), 0.32, 0.42);
  const railShortGeo = new THREE.BoxGeometry(0.42, 0.32, Math.max(5, d - 3));
  const corners: Array<[number, number]> = [
    [x + 1.8, z - d / 2 + 1],
    [x + w - 1.8, z - d / 2 + 1],
    [x + 1.8, z + d / 2 - 1],
    [x + w - 1.8, z + d / 2 - 1],
  ];
  for (const [px, pz] of corners) {
    const post = new THREE.Mesh(postGeo, postMat);
    post.position.set(px, 0.55, pz);
    post.castShadow = true;
    group.add(post);
  }
  [
    [cx, 0.92, z - d / 2 + 1, railLongGeo],
    [cx, 0.92, z + d / 2 - 1, railLongGeo],
    [x + 1.8, 0.92, z, railShortGeo],
    [x + w - 1.8, 0.92, z, railShortGeo],
  ].forEach(([px, py, pz, geo]) => {
    const rail = new THREE.Mesh(geo as THREE.BufferGeometry, railMat);
    rail.position.set(Number(px), Number(py), Number(pz));
    rail.castShadow = true;
    group.add(rail);
  });
  scene.add(group);
  const collisionStep = Math.max(12, Math.min(18, w / 4));
  for (let px = x + 9; px < x + w - 8; px += collisionStep) {
    addBlocker(px, z - d / 2 + 1, 0.85, 0.95);
    addBlocker(px, z + d / 2 - 1, 0.85, 0.95);
  }
}

function addBuildings(): void {
  addBuilding("MARS HAB", -122, -52, "#c85f3e", "#5b2b23", 22, 18);
  addBuilding("OXYGEN MILL", -86, -72, "#b97845", "#47251b", 17, 15);
  addBuilding("ICE VAULT", 34, -78, "#705f5a", "#26313a", 19, 16);
  addBuilding("WATER EXTRACTOR", 124, 54, "#7b7f85", "#314157", 15, 15);
  addBuilding("DUST MARKET", 92, -50, "#d06f3c", "#25362e", 22, 14);
  addBuilding("SEED LAB", -196, 58, "#bf7c46", "#4f2c20", 18, 14);
  addBuilding("RAID COMMS", 210, -64, "#8d5140", "#243632", 17, 13);
  addBuilding("ALGAE SILO", -22, 164, "#c7a66a", "#6d3628", 16, 16);
  addOrderBoard();
  addLeaderboardSign();
  addMarketZone();
  addStorageZone();
  addWell(-38, 72);
  addWell(224, 126);
  addSilo(-152, 68);
  addSilo(232, -126);
  addMarsLandmarks();
}

function addMarsLandmarks(): void {
  addDome("HYDROPONIC DOME", -42, -112, 13, "#5ad4ff");
  addDome("COLONY DOME", 176, -24, 15, "#a8f6ff");
  addDome("ICE DOME", -210, 138, 12, "#cdefff");
  addRocket(244, -150, 1.48, "PUMP ROCKET", "pump", -0.55);
  addRocket(-266, -214, 1.9, "SPACEX ROUTE", "space", 0.22);
  addRocket(268, 214, 1.66, "PUMP ROCKET", "pump", -0.55);
  addRocket(-270, 222, 1.55, "EARTH ROUTE", "space", 0.35);
  addRocket(258, -258, 1.32, "PUMP SHIP", "pump", -0.35);
  addLaunchGantry(-246, -214, 0.2);
  addLaunchGantry(248, 210, -0.55);
  addLaunchGantry(-248, 220, 0.45);
  addLaunchGantry(244, -252, -0.35);
  addWaterDeposit(-172, 214, "ICE WATER");
  addWaterDeposit(228, -92, "AQUIFER");
  addWaterDeposit(18, -262, "WATER FIND");
  addRover(-62, 64, 0.4);
  addRover(178, 132, -0.7);
  addSolarArray(-214, -76, 0.2);
  addSolarArray(-190, -82, 0.2);
  addSolarArray(52, 136, -0.25);
  addSolarArray(78, 142, -0.25);
  addAntenna(-248, -118);
  addAntenna(236, 30);
}

function addDome(label: string, x: number, z: number, radius: number, color: string): void {
  const group = new THREE.Group();
  const ring = new THREE.Mesh(new THREE.TorusGeometry(radius, 0.55, 8, 32), new THREE.MeshLambertMaterial({ color: "#4d3027" }));
  ring.rotation.x = Math.PI / 2;
  ring.position.y = 0.6;
  ring.castShadow = true;
  group.add(ring);

  const dome = new THREE.Mesh(
    new THREE.SphereGeometry(radius, 24, 12, 0, Math.PI * 2, 0, Math.PI / 2),
    new THREE.MeshLambertMaterial({ color, transparent: true, opacity: 0.28 }),
  );
  dome.position.y = 0.55;
  dome.castShadow = true;
  group.add(dome);

  const core = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.42, radius * 0.48, 3.2, 12), new THREE.MeshLambertMaterial({ color: "#294035" }));
  core.position.y = 1.7;
  core.castShadow = true;
  group.add(core);

  const sign = makeMountedSign(label, 280, 62, "#fff7df", "#4b251a", 26, 8.2, 1.65);
  sign.group.position.set(0, Math.max(5.4, radius * 0.54), radius * 0.76);
  group.add(sign.group);
  group.position.set(x, 0, z);
  scene.add(group);
  addBlocker(x, z, radius * 0.82, radius * 0.48);
}

function addRocket(x: number, z: number, scale = 1, label = "EARTH FLEET", variant: "pump" | "space" = "space", rot = -0.28): void {
  const group = new THREE.Group();
  const bodyColor = variant === "pump" ? "#f8fff4" : "#f3eee2";
  const accentColor = variant === "pump" ? "#1cb86f" : "#d85d35";
  const signBg = variant === "pump" ? "#1cb86f" : "#101418";
  const signColor = variant === "pump" ? "#f8fff4" : "#f0c542";
  const body = new THREE.Mesh(new THREE.CylinderGeometry(2.2, 2.4, 18, 18), new THREE.MeshLambertMaterial({ color: bodyColor }));
  body.position.y = 10.2;
  body.castShadow = true;
  group.add(body);
  const nose = new THREE.Mesh(new THREE.ConeGeometry(2.35, 5.6, 18), new THREE.MeshLambertMaterial({ color: accentColor }));
  nose.position.y = 22;
  nose.castShadow = true;
  group.add(nose);
  for (const side of [-1, 1]) {
    const fin = new THREE.Mesh(new THREE.BoxGeometry(0.7, 4.2, 3), new THREE.MeshLambertMaterial({ color: accentColor }));
    fin.position.set(side * 2.4, 3.6, 0.9);
    fin.rotation.z = side * 0.25;
    fin.castShadow = true;
    group.add(fin);
  }
  const stripe = new THREE.Mesh(new THREE.CylinderGeometry(2.24, 2.28, 0.8, 18), new THREE.MeshLambertMaterial({ color: accentColor }));
  stripe.position.y = 12.1;
  stripe.castShadow = true;
  group.add(stripe);
  const window = new THREE.Mesh(new THREE.SphereGeometry(0.82, 12, 8), new THREE.MeshLambertMaterial({ color: "#6bd7ff" }));
  window.position.set(0, 15.2, 2.1);
  window.scale.z = 0.25;
  group.add(window);
  const decal = makeRocketDecal(variant === "pump" ? "PUMP" : "X", variant === "pump" ? "#1cb86f" : "#101418", variant === "pump" ? "#f8fff4" : "#f0c542");
  decal.position.set(0, 12.15, 2.42);
  group.add(decal);
  const flame = new THREE.Mesh(new THREE.ConeGeometry(1.25, 4.8, 12), new THREE.MeshLambertMaterial({ color: "#f0c542" }));
  flame.position.y = 0.4;
  flame.rotation.x = Math.PI;
  group.add(flame);
  const sign = makeMountedSign(label, 240, 58, signColor, signBg, 24, 7.6, 1.55);
  sign.group.position.set(0, 8.3, 3.15);
  sign.group.rotation.x = -0.12;
  group.add(sign.group);
  group.position.set(x, 0, z);
  group.rotation.y = rot;
  group.scale.setScalar(scale);
  scene.add(group);
  addBlocker(x, z, 5.4 * scale, 19 * scale);
}

function makeRocketDecal(text: string, bg: string, fg: string): THREE.Mesh {
  const canvas2d = document.createElement("canvas");
  canvas2d.width = 180;
  canvas2d.height = 180;
  const ctx = canvas2d.getContext("2d")!;
  ctx.fillStyle = bg;
  ctx.beginPath();
  ctx.arc(90, 90, 72, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#101418";
  ctx.lineWidth = 10;
  ctx.stroke();
  ctx.fillStyle = fg;
  ctx.font = "900 44px ui-monospace, Menlo, Consolas, monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, 90, 94);
  if (text === "PUMP") {
    ctx.strokeStyle = "#f0c542";
    ctx.lineWidth = 8;
    ctx.beginPath();
    ctx.moveTo(45, 122);
    ctx.lineTo(70, 104);
    ctx.lineTo(92, 112);
    ctx.lineTo(132, 68);
    ctx.stroke();
  }
  const texture = new THREE.CanvasTexture(canvas2d);
  texture.minFilter = THREE.LinearFilter;
  texture.colorSpace = THREE.SRGBColorSpace;
  return new THREE.Mesh(
    new THREE.PlaneGeometry(3.15, 3.15),
    new THREE.MeshBasicMaterial({ map: texture, transparent: true }),
  );
}

function addLaunchGantry(x: number, z: number, rot: number): void {
  const group = new THREE.Group();
  const metal = new THREE.MeshLambertMaterial({ color: "#4d423a" });
  for (const dx of [-3.4, 3.4]) {
    const tower = new THREE.Mesh(new THREE.BoxGeometry(0.6, 18, 0.6), metal);
    tower.position.set(dx, 9, 0);
    tower.castShadow = true;
    group.add(tower);
  }
  for (let y = 3; y <= 16; y += 3.2) {
    const rail = new THREE.Mesh(new THREE.BoxGeometry(7.4, 0.36, 0.36), metal);
    rail.position.y = y;
    rail.castShadow = true;
    group.add(rail);
  }
  const arm = new THREE.Mesh(new THREE.BoxGeometry(8.6, 0.48, 0.48), new THREE.MeshLambertMaterial({ color: "#d18955" }));
  arm.position.set(3.5, 12.8, 1.6);
  arm.castShadow = true;
  group.add(arm);
  const sign = makeMountedSign("LAUNCH PAD", 220, 56, "#fff7df", "#4b251a", 24, 6.6, 1.45);
  sign.group.position.set(0, 19.2, 0.5);
  group.add(sign.group);
  group.position.set(x, 0, z);
  group.rotation.y = rot;
  scene.add(group);
  addBlocker(x, z, 5.5, 17);
}

function addRover(x: number, z: number, rot: number): void {
  const group = new THREE.Group();
  const body = new THREE.Mesh(new THREE.BoxGeometry(8.4, 1.8, 5.2), new THREE.MeshLambertMaterial({ color: "#d6b17a" }));
  body.position.y = 2.1;
  body.castShadow = true;
  group.add(body);
  const cab = new THREE.Mesh(new THREE.BoxGeometry(3.8, 2.1, 3.2), new THREE.MeshLambertMaterial({ color: "#7bbde8" }));
  cab.position.set(-1.2, 3.55, 0);
  cab.castShadow = true;
  group.add(cab);
  for (const wx of [-3.6, 0, 3.6]) {
    for (const wz of [-2.8, 2.8]) {
      const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.8, 0.8, 0.48, 12), new THREE.MeshLambertMaterial({ color: "#201715" }));
      wheel.position.set(wx, 1.15, wz);
      wheel.rotation.x = Math.PI / 2;
      wheel.castShadow = true;
      group.add(wheel);
    }
  }
  const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 3.8, 7), new THREE.MeshLambertMaterial({ color: "#2e2e2e" }));
  mast.position.set(3.3, 5.1, 0);
  group.add(mast);
  const dish = new THREE.Mesh(new THREE.ConeGeometry(1.1, 0.5, 12), new THREE.MeshLambertMaterial({ color: "#d8d4c8" }));
  dish.position.set(3.3, 7.05, 0);
  dish.rotation.x = Math.PI / 2;
  group.add(dish);
  group.position.set(x, 0, z);
  group.rotation.y = rot;
  scene.add(group);
  addBlocker(x, z, 5.2, 3.8);
}

function addSolarArray(x: number, z: number, rot: number): void {
  const group = new THREE.Group();
  const stand = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.22, 2.4, 6), new THREE.MeshLambertMaterial({ color: "#40332c" }));
  stand.position.y = 1.2;
  group.add(stand);
  const panel = new THREE.Mesh(new THREE.BoxGeometry(10, 0.25, 5.2), new THREE.MeshLambertMaterial({ color: "#1e4962" }));
  panel.position.y = 2.7;
  panel.rotation.x = -0.28;
  panel.castShadow = true;
  group.add(panel);
  for (let i = -3; i <= 3; i += 2) {
    const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.28, 5.35), new THREE.MeshLambertMaterial({ color: "#6bd7ff" }));
    stripe.position.set(i, 2.86, 0);
    stripe.rotation.x = -0.28;
    group.add(stripe);
  }
  group.position.set(x, 0, z);
  group.rotation.y = rot;
  scene.add(group);
  addBlocker(x, z, 5.8, 1.8);
}

function addAntenna(x: number, z: number): void {
  const group = new THREE.Group();
  const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.28, 10, 8), new THREE.MeshLambertMaterial({ color: "#3d352f" }));
  mast.position.y = 5;
  mast.castShadow = true;
  group.add(mast);
  const dish = new THREE.Mesh(new THREE.ConeGeometry(2.4, 0.9, 18), new THREE.MeshLambertMaterial({ color: "#c7b6a2" }));
  dish.position.y = 10.3;
  dish.rotation.x = Math.PI / 2.5;
  dish.castShadow = true;
  group.add(dish);
  const light = new THREE.Mesh(new THREE.SphereGeometry(0.42, 8, 6), new THREE.MeshLambertMaterial({ color: "#57d96f" }));
  light.position.y = 11.4;
  group.add(light);
  group.position.set(x, 0, z);
  scene.add(group);
  addBlocker(x, z, 1.8, 9);
}


function addBuilding(label: string, x: number, z: number, wall: string, roof: string, w: number, d: number): void {
  const base = new THREE.Mesh(new THREE.BoxGeometry(w, 5.8, d), new THREE.MeshLambertMaterial({ color: wall }));
  base.position.set(x, 2.9, z);
  base.castShadow = true;
  base.receiveShadow = true;
  scene.add(base);

  const roofMesh = new THREE.Mesh(new THREE.ConeGeometry(Math.max(w, d) * 0.74, 4.2, 4), new THREE.MeshLambertMaterial({ color: roof }));
  roofMesh.position.set(x, 7.8, z);
  roofMesh.rotation.y = Math.PI / 4;
  roofMesh.castShadow = true;
  scene.add(roofMesh);

  const mountedSign = makeMountedSign(label, 280, 76, "#f8efd8", "#168f52", 32, Math.min(12, w * 0.68), 2.35);
  mountedSign.group.position.set(x, 8.75, z + d / 2 + 0.62);
  scene.add(mountedSign.group);
  addBlocker(x, z, Math.max(w, d) * 0.62 + 1.5, 7);

  if (["OXYGEN MILL", "ICE VAULT", "DUST MARKET"].includes(label)) {
    const prompt = makeTextSprite("Collect Yield", 240, 62, "#f7f1dc", "#d77d27", 28);
    prompt.position.set(x, 11.2, z);
    prompt.scale.set(6.2, 1.55, 1);
    prompt.visible = false;
    yieldPrompts.push(prompt);
    scene.add(prompt);
  }
}

function addOrderBoard(): void {
  const board = new THREE.Group();
  board.name = "order-board";
  const postMat = new THREE.MeshLambertMaterial({ color: "#78451f" });
  const panelMat = new THREE.MeshLambertMaterial({ color: "#efe4c7" });
  const panel = new THREE.Mesh(new THREE.BoxGeometry(12, 8, 0.55), panelMat);
  panel.position.y = 5.4;
  panel.castShadow = true;
  board.add(panel);
  for (const x of [-5.4, 5.4]) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.6, 8.5, 0.6), postMat);
    post.position.set(x, 3.6, -0.2);
    post.castShadow = true;
    board.add(post);
  }
  const sign = makeMountedSign("MISSION BOARD", 290, 70, "#101418", "#f0c542", 30, 9.4, 2);
  sign.group.position.set(0, 8.55, 0.48);
  board.add(sign.group);
  board.position.set(90, 0, 18);
  board.rotation.y = -0.55;
  scene.add(board);
  addBlocker(90, 18, 7.5, 8);
}

function addLeaderboardSign(): void {
  const sign = new THREE.Group();
  const panel = new THREE.Mesh(new THREE.BoxGeometry(15, 9, 0.6), new THREE.MeshLambertMaterial({ color: "#1a2222" }));
  panel.position.y = 5.6;
  panel.castShadow = true;
  sign.add(panel);
  const text = makeMountedSign("TOP COLONISTS", 290, 70, "#57ff91", "#101418", 30, 10.6, 2.25);
  text.group.position.set(0, 8.7, 0.48);
  sign.add(text.group);
  sign.position.set(-28, 0, 38);
  sign.rotation.y = 0.6;
  scene.add(sign);
  addBlocker(-28, 38, 8.5, 8);
}

function addDecor(): void {
  for (let i = 0; i < 260; i += 1) {
    const edge = i % 4;
    const x = edge < 2 ? seeded(i * 17.17, -292, 292) : (edge === 2 ? -285 + seeded(i, -18, 18) : 285 + seeded(i, -18, 18));
    const z = edge < 2 ? (edge === 0 ? -282 + seeded(i, -20, 20) : 282 + seeded(i, -20, 20)) : seeded(i * 41.93, -285, 285);
    scene.add(i % 5 === 0 ? makeRock(x, z) : makeTree(x, z, 1 + (i % 5) * 0.1));
  }

  for (let i = 0; i < 54; i += 1) {
    const x = -262 + (i % 9) * 16 + seeded(i * 9.13, -2, 2);
    const z = 28 + Math.floor(i / 9) * 22 + seeded(i * 3.91, -2, 2);
    scene.add(i % 6 === 0 ? makeRock(x, z) : makeTree(x, z, 0.72 + (i % 4) * 0.08));
  }

  for (let i = 0; i < 46; i += 1) {
    const x = 174 + (i % 7) * 14 + seeded(i * 8.77, -1.8, 1.8);
    const z = -18 + Math.floor(i / 7) * 22 + seeded(i * 6.33, -1.8, 1.8);
    scene.add(i % 5 === 0 ? makeRock(x, z) : makeTree(x, z, 0.66 + (i % 3) * 0.1));
  }

  for (let x = -248; x <= 248; x += 12) {
    addFencePost(x, -190);
    addFencePost(x, 202);
  }
  for (let z = -180; z <= 192; z += 12) {
    addFencePost(-258, z);
    addFencePost(258, z);
  }

  for (let x = -232; x <= 226; x += 18) {
    addProp("lamp", x, -214, 0);
    if (x % 36 === 0) addProp("banner", x + 6, 216, Math.PI);
  }
  for (let z = -166; z <= 178; z += 24) {
    addProp("lamp", -284, z, Math.PI / 2);
    addProp("lamp", 284, z, -Math.PI / 2);
  }

  const decor: Array<[string, number, number, number?]> = [
    ["hay", -105, -34], ["hay", -113, -30], ["hay", -96, -29],
    ["cart", -56, -18, 0.4], ["cart", 112, -22, -0.4],
    ["barrel", 70, -42], ["barrel", 80, -45], ["barrel", 162, 34],
    ["sack", 122, -42], ["sack", 130, -38], ["sack", 138, -45],
    ["logs", -142, 30, 0.7], ["logs", -154, 24, -0.3],
    ["scarecrow", -165, -116], ["scarecrow", 188, 88],
    ["pumpkins", 56, 22], ["pumpkins", -216, 104], ["pumpkins", 184, -102],
    ["hay", -228, -194], ["hay", -216, -198], ["barrel", -186, -204], ["cart", -136, -204, 0.2],
    ["pumpkins", -86, -214], ["sack", -34, -210], ["barrel", 16, -212], ["cart", 70, -212, -0.15],
    ["hay", 142, -204], ["pumpkins", 210, -202], ["sack", 246, -206],
    ["logs", -292, -126, 0.2], ["barrel", -292, -82], ["sack", -292, -34], ["pumpkins", -292, 20],
    ["cart", -292, 74, 1.2], ["hay", -292, 126], ["logs", -292, 170, -0.4],
    ["logs", 292, -142, -0.4], ["pumpkins", 292, -88], ["sack", 292, -30], ["cart", 292, 38, -1.2],
    ["barrel", 292, 92], ["hay", 292, 148], ["pumpkins", 292, 188],
    ["scarecrow", -214, -170], ["scarecrow", -78, -168], ["scarecrow", 82, -170],
    ["scarecrow", -220, 150], ["scarecrow", -86, 170], ["scarecrow", 82, 170], ["scarecrow", 206, 166],
    ["crate", -12, 34], ["crate", 8, 36], ["barrel", 24, 38], ["sack", -28, 44],
    ["banner", -48, -22, 0.45], ["banner", 46, 26, -0.55], ["banner", 132, 6, -0.5],
    ["crate", -246, -226], ["crate", -210, -226], ["barrel", -174, -226], ["sack", -138, -226],
    ["logs", -92, -226, 0.2], ["crate", -42, -226], ["barrel", 16, -226], ["sack", 64, -226],
    ["cart", 126, -226, -0.12], ["crate", 184, -226], ["barrel", 238, -226],
    ["crate", -246, 214], ["barrel", -206, 214], ["sack", -162, 214], ["logs", -110, 214, -0.2],
    ["cart", -48, 214, 0.15], ["crate", 24, 214], ["barrel", 86, 214], ["sack", 144, 214],
    ["crate", 202, 214], ["hay", 246, 214],
    ["terminal", -18, -38, 0.1], ["terminal", 74, 18, -0.35], ["terminal", 206, -86, 0.2],
    ["fuel", -236, -246, 0.2], ["fuel", -198, -246, 0.2], ["fuel", 214, -246, -0.2],
    ["fuel", -236, 238, 0.2], ["fuel", 224, 238, -0.2],
    ["beacon", -92, -246], ["beacon", 118, -246], ["beacon", -148, 236], ["beacon", 132, 236],
    ["jumpPad", -12, -82], ["jumpPad", 114, 78], ["jumpPad", -188, 122], ["jumpPad", 210, 156],
  ];
  decor.forEach(([kind, x, z, rot = 0]) => addProp(kind, x, z, rot));
  [
    [-24, -18, 0.15], [22, -18, -0.15], [-54, 18, -0.4], [56, 18, 0.4],
    [-178, -72, 0.08], [-136, -92, -0.15], [146, -72, 0.12], [194, -24, -0.22],
    [-178, 76, -0.3], [-92, 112, 0.24], [82, 126, -0.1], [188, 138, 0.2],
    [-246, -214, 0.08], [-214, -214, 0.08], [-182, -214, 0.08], [-150, -214, 0.08],
    [-118, -214, 0.08], [-86, -214, 0.08], [-54, -214, 0.08], [-22, -214, 0.08],
    [42, -214, -0.08], [74, -214, -0.08], [106, -214, -0.08], [138, -214, -0.08],
    [170, -214, -0.08], [202, -214, -0.08], [234, -214, -0.08],
    [-244, 208, -0.08], [-212, 208, -0.08], [-180, 208, -0.08], [-148, 208, -0.08],
    [-116, 208, -0.08], [-84, 208, -0.08], [-52, 208, -0.08], [-20, 208, -0.08],
    [44, 208, 0.08], [76, 208, 0.08], [108, 208, 0.08], [140, 208, 0.08],
    [172, 208, 0.08], [204, 208, 0.08], [236, 208, 0.08],
    [-258, -152, Math.PI / 2], [-258, -112, Math.PI / 2], [-258, -72, Math.PI / 2],
    [-258, 70, Math.PI / 2], [-258, 110, Math.PI / 2], [-258, 150, Math.PI / 2],
    [258, -152, Math.PI / 2], [258, -112, Math.PI / 2], [258, -72, Math.PI / 2],
    [258, 70, Math.PI / 2], [258, 110, Math.PI / 2], [258, 150, Math.PI / 2],
  ].forEach(([x, z, rot]) => addHurdle(x, z, Number(rot)));
  addFieldBarriers();

  addWoodSign("HYDRO WHEAT", -228, -210, 0.1);
  addWoodSign("RED TUBERS", 118, -210, -0.1);
  addWoodSign("DOME GROVE", 224, 16, -0.45);
  addWoodSign("OXYGEN FIELDS", -224, 90, 0.35);
  addWoodSign("ICE ROW", -48, 126, 0);
  addWoodSign("RAID CROPS", 124, 126, -0.15);
}

function addBonuses(): void {
  const spots: Array<[BonusType, number, number, number]> = [
    ["coin", -18, -24, 35], ["boost", 26, -24, 45], ["crate", -42, 24, 55], ["coin", 48, 24, 35],
    ["coin", -210, -176, 35], ["boost", -138, -148, 45], ["crate", -56, -178, 55],
    ["coin", 92, -178, 35], ["boost", 184, -158, 45], ["crate", 242, -118, 60],
    ["coin", -238, -34, 35], ["boost", -208, 112, 45], ["crate", -152, 152, 60],
    ["coin", -48, 118, 35], ["boost", 28, 126, 45], ["crate", 94, 158, 60],
    ["coin", 174, 70, 35], ["boost", 226, 106, 45], ["crate", 246, 170, 60],
    ["boost", -266, -236, 50], ["coin", -226, -244, 38], ["crate", -174, -238, 64],
    ["coin", -96, -244, 38], ["boost", -24, -238, 52], ["coin", 54, -244, 38],
    ["crate", 132, -238, 64], ["boost", 214, -236, 52], ["coin", 264, -208, 38],
    ["crate", -264, 236, 64], ["boost", -206, 232, 52], ["coin", -132, 238, 38],
    ["crate", -46, 232, 64], ["boost", 46, 238, 52], ["coin", 126, 232, 38],
    ["crate", 206, 238, 64], ["boost", 266, 204, 52],
  ];
  spots.forEach(([type, x, z, value], index) => {
    const bonus: BonusState = { id: `bonus-${index}`, type, x, z, ready: true, value };
    bonuses.push(bonus);
    const mesh = createBonusMesh(bonus);
    bonusMeshes.set(bonus.id, mesh);
    scene.add(mesh);
  });
}

function createBonusMesh(bonus: BonusState): THREE.Group {
  const group = new THREE.Group();
  const color = bonus.type === "coin" ? "#f0c542" : bonus.type === "boost" ? "#57d96f" : "#d77d27";
  if (bonus.type === "coin") {
    const coin = new THREE.Mesh(new THREE.CylinderGeometry(1.15, 1.15, 0.28, 18), new THREE.MeshLambertMaterial({ color }));
    coin.rotation.x = Math.PI / 2;
    coin.position.y = 1.35;
    coin.castShadow = true;
    group.add(coin);
  } else if (bonus.type === "boost") {
    const gem = new THREE.Mesh(new THREE.OctahedronGeometry(1.15, 0), new THREE.MeshLambertMaterial({ color }));
    gem.position.y = 1.55;
    gem.castShadow = true;
    group.add(gem);
  } else {
    const crate = new THREE.Mesh(new THREE.BoxGeometry(2.1, 1.6, 2.1), new THREE.MeshLambertMaterial({ color: "#9b6a35" }));
    crate.position.y = 0.82;
    crate.castShadow = true;
    group.add(crate);
    const band = new THREE.Mesh(new THREE.BoxGeometry(2.24, 0.22, 0.22), new THREE.MeshLambertMaterial({ color: "#57d96f" }));
    band.position.set(0, 1.25, 1.16);
    group.add(band);
  }
  const label = makeTextSprite(bonus.type === "crate" ? "TASK" : "BONUS", 170, 50, "#fff8e7", "#29312f", 22);
  label.position.y = 3.1;
  label.scale.set(3.1, 0.9, 1);
  group.add(label);
  group.position.set(bonus.x, 0, bonus.z);
  return group;
}

function addDemoNpcs(): void {
  const names = TRENCHER_NAMES;
  const colors = ["#e85d75", "#4b89ff", "#8d5cff", "#ff8c30", "#23a76d", "#f0c542", "#57d96f", "#2f67b1", "#d68b22", "#17211c", "#b455e8", "#e15a3c"];
  names.forEach((name, index) => {
    const path = makeNpcPath(index);
    const player: PlayerState = {
      id: `demo-${index}`,
      name,
      x: path[0].x,
      y: 0,
      z: path[0].z,
      rot: 0,
      color: colors[index % colors.length],
      hat: index % 4 === 0 ? "yellow" : index % 4 === 1 ? "green" : index % 4 === 2 ? "white" : "black",
      harvested: 0,
      cash: 0,
      wallet: "",
      holder: index % 3 === 0,
    };
    const rig = createPlayerRig(player);
    scene.add(rig.group);
    demoNpcs.push({
      rig,
      path,
      pathIndex: 1,
      speed: 16 + (index % 7) * 1.45,
      jumpTimer: 0.35 + index * 0.13,
      taskTimer: 1.2 + index * 0.25,
      effectTimer: 0.8 + index * 0.18,
      name,
    });
  });
}

function makeNpcPath(index: number): THREE.Vector3[] {
  const routeSets = [
    [[-30, -24], [50, -24], [68, 32], [-36, 44], [-12, -8]],
    [[-238, -184], [-128, -176], [-78, -124], [-222, -98], [-260, -142]],
    [[74, -190], [214, -176], [250, -110], [104, -92], [22, -132]],
    [[-258, -46], [-194, 22], [-256, 128], [-210, 186], [-282, 68]],
    [[-116, 126], [-28, 82], [72, 118], [-24, 194], [-142, 164]],
    [[152, 26], [252, 70], [258, 174], [158, 190], [108, 110]],
    [[-126, -28], [-68, -78], [-18, -62], [-54, 16], [-118, 24]],
    [[92, -52], [146, -28], [98, 34], [50, 0], [124, 18]],
  ];
  const base = routeSets[index % routeSets.length];
  const jitter = index * 17.31;
  return base.map(([x, z], pointIndex) => new THREE.Vector3(
    x + seeded(jitter + pointIndex, -12, 12),
    0,
    z + seeded(jitter + pointIndex * 3.3, -12, 12),
  ));
}

function addMarketZone(): void {
  [
    ["O2", 58, -28, "#c85b3a"],
    ["ICE", 78, -26, "#2f8a55"],
    ["ROVERS", 98, -28, "#2f67b1"],
    ["MEMES", 118, -30, "#dd9a2f"],
  ].forEach(([label, x, z, color], index) => {
    const group = new THREE.Group();
    const base = new THREE.Mesh(new THREE.BoxGeometry(10, 3.2, 6), new THREE.MeshLambertMaterial({ color: "#8a5428" }));
    base.position.y = 1.6;
    base.castShadow = true;
    group.add(base);
    const roof = new THREE.Mesh(new THREE.BoxGeometry(12, 0.5, 7.5), new THREE.MeshLambertMaterial({ color: String(color) }));
    roof.position.y = 3.55;
    roof.castShadow = true;
    group.add(roof);
    const sign = makeMountedSign(String(label), 180, 58, "#f7f1dc", "#27352d", 28, 5.6, 1.45);
    sign.group.position.set(0, 4.45, 3.92);
    group.add(sign.group);
    group.position.set(Number(x), 0, Number(z) + index * 4);
    group.rotation.y = -0.22;
    scene.add(group);
    addBlocker(Number(x), Number(z) + index * 4, 6.2, 3.8);
  });
}

function addStorageZone(): void {
  for (let i = 0; i < 16; i += 1) {
    addProp(i % 3 === 0 ? "barrel" : i % 3 === 1 ? "crate" : "sack", 142 + (i % 4) * 8, 116 + Math.floor(i / 4) * 7, i * 0.2);
  }
  addWoodSign("CARGO BAY", 155, 104, -0.15);
}

function addWell(x: number, z: number): void {
  const group = new THREE.Group();
  const stone = new THREE.Mesh(new THREE.CylinderGeometry(3.4, 3.8, 2.2, 10), new THREE.MeshLambertMaterial({ color: "#6b6f74" }));
  stone.position.y = 1.1;
  stone.castShadow = true;
  group.add(stone);
  const pump = new THREE.Mesh(new THREE.CylinderGeometry(0.8, 0.9, 5.4, 10), new THREE.MeshLambertMaterial({ color: "#2f67b1" }));
  pump.position.y = 4.3;
  pump.castShadow = true;
  group.add(pump);
  const pipe = new THREE.Mesh(new THREE.BoxGeometry(5.8, 0.55, 0.55), new THREE.MeshLambertMaterial({ color: "#c9c2b3" }));
  pipe.position.set(1.8, 6.7, 0);
  pipe.castShadow = true;
  group.add(pipe);
  const waterGlow = new THREE.Mesh(new THREE.SphereGeometry(1.1, 10, 8), new THREE.MeshLambertMaterial({ color: "#6bd7ff" }));
  waterGlow.position.y = 7.1;
  group.add(waterGlow);
  group.position.set(x, 0, z);
  scene.add(group);
  addBlocker(x, z, 4.8, 5.5);
}

function addWaterDeposit(x: number, z: number, label: string): void {
  const group = new THREE.Group();
  const ice = new THREE.Mesh(new THREE.CylinderGeometry(8.8, 10.2, 0.5, 12), new THREE.MeshLambertMaterial({ color: "#6bd7ff", transparent: true, opacity: 0.82 }));
  ice.position.y = 0.35;
  ice.scale.z = 0.72;
  ice.castShadow = true;
  group.add(ice);

  const rim = new THREE.Mesh(new THREE.TorusGeometry(7.7, 0.38, 7, 36), new THREE.MeshLambertMaterial({ color: "#d7cbb6" }));
  rim.position.y = 0.78;
  rim.rotation.x = Math.PI / 2;
  rim.scale.z = 0.72;
  group.add(rim);

  for (let i = 0; i < 6; i += 1) {
    const angle = (i / 6) * Math.PI * 2;
    const shard = new THREE.Mesh(new THREE.ConeGeometry(0.8, 3.2 + (i % 2) * 1.1, 5), new THREE.MeshLambertMaterial({ color: i % 2 ? "#cdefff" : "#6bd7ff", transparent: true, opacity: 0.9 }));
    shard.position.set(Math.cos(angle) * 5.8, 2, Math.sin(angle) * 4.1);
    shard.rotation.z = Math.sin(angle) * 0.22;
    shard.castShadow = true;
    group.add(shard);
  }

  const pump = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.6, 4.8, 9), new THREE.MeshLambertMaterial({ color: "#2f67b1" }));
  pump.position.set(-5.6, 2.6, -1.4);
  pump.castShadow = true;
  group.add(pump);

  const sign = makeMountedSign(label, 220, 58, "#102028", "#6bd7ff", 24, 6.8, 1.45);
  sign.group.position.set(0, 4.6, 6.2);
  group.add(sign.group);
  group.position.set(x, 0, z);
  scene.add(group);
  addBlocker(x, z, 8.6, 3.2);
}

function addSilo(x: number, z: number): void {
  const group = new THREE.Group();
  const body = new THREE.Mesh(new THREE.CylinderGeometry(4.2, 4.4, 13, 12), new THREE.MeshLambertMaterial({ color: "#c7b7a2" }));
  body.position.y = 6.5;
  body.castShadow = true;
  body.receiveShadow = true;
  group.add(body);
  const roof = new THREE.Mesh(new THREE.ConeGeometry(4.7, 3.6, 12), new THREE.MeshLambertMaterial({ color: "#5a6672" }));
  roof.position.y = 14.9;
  roof.castShadow = true;
  group.add(roof);
  const text = makeMountedSign("OXYGEN", 180, 54, "#f7f1dc", "#2ba85d", 28, 5.4, 1.35);
  text.group.position.set(0, 8.85, 4.62);
  group.add(text.group);
  group.position.set(x, 0, z);
  scene.add(group);
  addBlocker(x, z, 5.1, 13);
}

function addWoodSign(text: string, x: number, z: number, rot: number): void {
  const group = new THREE.Group();
  const post = new THREE.Mesh(new THREE.BoxGeometry(0.6, 4, 0.6), new THREE.MeshLambertMaterial({ color: "#4b352e" }));
  post.position.y = 2;
  post.castShadow = true;
  group.add(post);
  const label = makeMountedSign(text, 220, 60, "#f7f1dc", "#4b251a", 26, 8.6, 2.2);
  label.group.position.set(0, 4.15, 0.25);
  group.add(label.group);
  group.position.set(x, 0, z);
  group.rotation.y = rot;
  scene.add(group);
  addBlocker(x, z, 4.4, 3.6);
}

function addProp(kind: string, x: number, z: number, rot = 0): void {
  const group = new THREE.Group();
  if (kind === "hay") {
    const bale = new THREE.Mesh(new THREE.BoxGeometry(4.6, 2.4, 3.2), new THREE.MeshLambertMaterial({ color: "#c6a06b" }));
    bale.position.y = 1.2;
    bale.castShadow = true;
    group.add(bale);
    const stripe = new THREE.Mesh(new THREE.BoxGeometry(4.8, 0.28, 3.35), new THREE.MeshLambertMaterial({ color: "#6bd7ff" }));
    stripe.position.y = 2.15;
    group.add(stripe);
  } else if (kind === "cart") {
    const cart = new THREE.Mesh(new THREE.BoxGeometry(7, 1.3, 4), new THREE.MeshLambertMaterial({ color: "#7f6655" }));
    cart.position.y = 1.2;
    cart.castShadow = true;
    group.add(cart);
    [-3, 3].forEach((wx) => {
      const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 0.9, 0.45, 10), new THREE.MeshLambertMaterial({ color: "#201715" }));
      wheel.position.set(wx, 0.8, 2.15);
      wheel.rotation.z = Math.PI / 2;
      wheel.castShadow = true;
      group.add(wheel);
    });
  } else if (kind === "barrel") {
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(1.2, 1.2, 2.6, 10), new THREE.MeshLambertMaterial({ color: "#6d8da2" }));
    barrel.position.y = 1.3;
    barrel.castShadow = true;
    group.add(barrel);
    const band = new THREE.Mesh(new THREE.CylinderGeometry(1.24, 1.24, 0.24, 10), new THREE.MeshLambertMaterial({ color: "#6bd7ff" }));
    band.position.y = 1.78;
    group.add(band);
  } else if (kind === "crate") {
    const crate = new THREE.Mesh(new THREE.BoxGeometry(3.2, 2.6, 3.2), new THREE.MeshLambertMaterial({ color: "#b77b48" }));
    crate.position.y = 1.3;
    crate.castShadow = true;
    group.add(crate);
  } else if (kind === "sack") {
    const sack = new THREE.Mesh(new THREE.SphereGeometry(1.45, 9, 7), new THREE.MeshLambertMaterial({ color: "#cfc4b7" }));
    sack.position.y = 1.2;
    sack.scale.set(0.9, 1.2, 0.75);
    sack.castShadow = true;
    group.add(sack);
  } else if (kind === "logs") {
    for (let i = 0; i < 4; i += 1) {
      const log = new THREE.Mesh(new THREE.CylinderGeometry(0.65, 0.65, 6, 8), new THREE.MeshLambertMaterial({ color: "#737b82" }));
      log.position.set(0, 0.65 + i * 0.55, (i % 2) * 0.7);
      log.rotation.z = Math.PI / 2;
      log.castShadow = true;
      group.add(log);
    }
  } else if (kind === "scarecrow") {
    const pole = new THREE.Mesh(new THREE.BoxGeometry(0.45, 5.8, 0.45), new THREE.MeshLambertMaterial({ color: "#4d423a" }));
    pole.position.y = 2.9;
    group.add(pole);
    const arm = new THREE.Mesh(new THREE.BoxGeometry(5.2, 0.35, 0.35), new THREE.MeshLambertMaterial({ color: "#4d423a" }));
    arm.position.y = 4.1;
    group.add(arm);
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.9, 8, 6), new THREE.MeshLambertMaterial({ color: "#57d96f" }));
    head.position.y = 5.2;
    group.add(head);
    const dish = new THREE.Mesh(new THREE.ConeGeometry(1.1, 0.45, 10), new THREE.MeshLambertMaterial({ color: "#c7b6a2" }));
    dish.position.y = 6.15;
    dish.rotation.x = Math.PI / 2;
    group.add(dish);
  } else if (kind === "pumpkins") {
    for (let i = 0; i < 5; i += 1) {
      const p = new THREE.Mesh(new THREE.DodecahedronGeometry(1.1 + (i % 2) * 0.35, 0), new THREE.MeshLambertMaterial({ color: i % 2 ? "#8f4632" : "#b8623d" }));
      p.position.set((i - 2) * 2.1, 0.85, Math.sin(i) * 1.2);
      p.scale.y = 0.7;
      p.castShadow = true;
      group.add(p);
    }
  } else if (kind === "lamp") {
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.24, 5.2, 7), new THREE.MeshLambertMaterial({ color: "#4d423a" }));
    post.position.y = 2.6;
    post.castShadow = true;
    group.add(post);
    const cap = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.28, 1.1), new THREE.MeshLambertMaterial({ color: "#2c2418" }));
    cap.position.y = 5.1;
    cap.castShadow = true;
    group.add(cap);
    const glow = new THREE.Mesh(new THREE.SphereGeometry(0.45, 8, 6), new THREE.MeshLambertMaterial({ color: "#6bd7ff" }));
    glow.position.y = 4.72;
    group.add(glow);
  } else if (kind === "banner") {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.38, 5.2, 0.38), new THREE.MeshLambertMaterial({ color: "#4d423a" }));
    post.position.y = 2.6;
    post.castShadow = true;
    group.add(post);
    const cloth = new THREE.Mesh(new THREE.BoxGeometry(3.4, 2.1, 0.16), new THREE.MeshLambertMaterial({ color: "#d96a36" }));
    cloth.position.set(1.7, 4.2, 0);
    cloth.castShadow = true;
    group.add(cloth);
    const stripe = new THREE.Mesh(new THREE.BoxGeometry(2.8, 0.28, 0.18), new THREE.MeshLambertMaterial({ color: "#6bd7ff" }));
    stripe.position.set(1.72, 4.2, 0.1);
    group.add(stripe);
  } else if (kind === "terminal") {
    const base = new THREE.Mesh(new THREE.BoxGeometry(4.4, 2.6, 2.8), new THREE.MeshLambertMaterial({ color: "#33413b" }));
    base.position.y = 1.3;
    base.castShadow = true;
    group.add(base);
    const screen = new THREE.Mesh(new THREE.BoxGeometry(3.2, 1.4, 0.16), new THREE.MeshLambertMaterial({ color: "#57d96f" }));
    screen.position.set(0, 2.12, 1.46);
    screen.rotation.x = -0.12;
    group.add(screen);
    const buttons = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.18, 0.7), new THREE.MeshLambertMaterial({ color: "#f0c542" }));
    buttons.position.set(0, 0.98, 1.55);
    group.add(buttons);
  } else if (kind === "fuel") {
    const tank = new THREE.Mesh(new THREE.CylinderGeometry(1.1, 1.1, 5.6, 12), new THREE.MeshLambertMaterial({ color: "#f4eee2" }));
    tank.position.y = 2.8;
    tank.rotation.z = Math.PI / 2;
    tank.castShadow = true;
    group.add(tank);
    const stripe = new THREE.Mesh(new THREE.CylinderGeometry(1.13, 1.13, 0.36, 12), new THREE.MeshLambertMaterial({ color: "#1cb86f" }));
    stripe.position.y = 2.8;
    stripe.rotation.z = Math.PI / 2;
    group.add(stripe);
  } else if (kind === "beacon") {
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.24, 6.8, 7), new THREE.MeshLambertMaterial({ color: "#3f332d" }));
    pole.position.y = 3.4;
    pole.castShadow = true;
    group.add(pole);
    const ring = new THREE.Mesh(new THREE.TorusGeometry(1.15, 0.1, 6, 18), new THREE.MeshLambertMaterial({ color: "#f0c542" }));
    ring.position.y = 6.55;
    ring.rotation.x = Math.PI / 2;
    group.add(ring);
    const light = new THREE.Mesh(new THREE.SphereGeometry(0.62, 10, 8), new THREE.MeshLambertMaterial({ color: "#57d96f" }));
    light.position.y = 6.55;
    group.add(light);
  } else if (kind === "jumpPad") {
    const pad = new THREE.Mesh(new THREE.CylinderGeometry(3.2, 3.6, 0.35, 12), new THREE.MeshLambertMaterial({ color: "#29312f" }));
    pad.position.y = 0.2;
    pad.castShadow = true;
    group.add(pad);
    const arrow = new THREE.Mesh(new THREE.ConeGeometry(1.2, 2.2, 3), new THREE.MeshLambertMaterial({ color: "#57d96f" }));
    arrow.position.y = 0.72;
    arrow.rotation.x = Math.PI / 2;
    group.add(arrow);
  }
  group.position.set(x, 0, z);
  group.rotation.y = rot;
  scene.add(group);
  const blockerRadius = {
    hay: 2.8,
    cart: 4.2,
    barrel: 1.55,
    crate: 2,
    sack: 1.65,
    logs: 3.2,
    scarecrow: 1.6,
    pumpkins: 3.8,
    lamp: 0.85,
    banner: 1.2,
    terminal: 2.7,
    fuel: 2.9,
    beacon: 1.2,
  }[kind];
  if (blockerRadius) addBlocker(x, z, blockerRadius, kind === "lamp" || kind === "banner" ? 1.8 : 2.8);
}

function addHurdle(x: number, z: number, rot = 0): void {
  const group = new THREE.Group();
  const postMat = new THREE.MeshLambertMaterial({ color: "#6e421e" });
  const railMat = new THREE.MeshLambertMaterial({ color: "#d5ad44" });
  [-3.8, 3.8].forEach((px) => {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.45, 1.3, 0.45), postMat);
    post.position.set(px, 0.65, 0);
    post.castShadow = true;
    group.add(post);
  });
  const rail = new THREE.Mesh(new THREE.BoxGeometry(8.4, 0.42, 0.5), railMat);
  rail.position.y = 1.15;
  rail.castShadow = true;
  group.add(rail);
  group.position.set(x, 0, z);
  group.rotation.y = rot;
  scene.add(group);

  for (let i = -3; i <= 3; i += 3) {
    const px = x + Math.cos(rot) * i;
    const pz = z - Math.sin(rot) * i;
    addBlocker(px, pz, 1.15, 1.25);
  }
}

function addFieldBarriers(): void {
  [
    [-193, -142, 112, 104],
    [-48, -158, 112, 84],
    [114, -146, 132, 104],
    [-208, -30, 118, 64],
    [-18, -30, 112, 74],
    [210, 74, 116, 102],
    [-202, 140, 112, 88],
    [-68, 164, 112, 72],
    [82, 164, 118, 72],
    [194, 164, 110, 72],
    [-160, -239, 194, 32],
    [78, -239, 226, 32],
    [-160, 232, 194, 32],
    [82, 232, 230, 32],
  ].forEach(([x, z, w, d]) => addFieldBarrier(Number(x), Number(z), Number(w), Number(d)));
}

function addFieldBarrier(cx: number, cz: number, w: number, d: number): void {
  const step = 10.5;
  const left = cx - w / 2;
  const right = cx + w / 2;
  const top = cz - d / 2;
  const bottom = cz + d / 2;
  const skipGate = (value: number, center: number) => Math.abs(value - center) < 11;

  for (let x = left + 6; x <= right - 6; x += step) {
    if (!skipGate(x, cx)) addHurdle(x, top, 0);
    if (!skipGate(x, cx)) addHurdle(x, bottom, 0);
  }
  for (let z = top + 6; z <= bottom - 6; z += step) {
    if (!skipGate(z, cz)) addHurdle(left, z, Math.PI / 2);
    if (!skipGate(z, cz)) addHurdle(right, z, Math.PI / 2);
  }
}

function addBlocker(x: number, z: number, radius: number, height: number): void {
  worldBlockers.push({ x, z, radius, height });
}

function addFencePost(x: number, z: number): void {
  const mat = new THREE.MeshLambertMaterial({ color: "#75421d" });
  const post = new THREE.Mesh(new THREE.BoxGeometry(0.65, 2.8, 0.65), mat);
  post.position.set(x, 1.4, z);
  post.castShadow = true;
  scene.add(post);
}

function makeTree(x: number, z: number, scale: number): THREE.Group {
  const group = new THREE.Group();
  const base = new THREE.Mesh(new THREE.CylinderGeometry(1.4, 1.8, 0.7, 8), new THREE.MeshLambertMaterial({ color: "#6c3828" }));
  base.position.y = 0.35;
  base.castShadow = true;
  group.add(base);
  const colors = ["#57d96f", "#6bd7ff", "#f0c542"];
  for (let i = 0; i < 3; i += 1) {
    const crystal = new THREE.Mesh(
      new THREE.ConeGeometry(0.8 + i * 0.25, 4.2 + i * 1.1, 5),
      new THREE.MeshLambertMaterial({ color: colors[i % colors.length], transparent: true, opacity: 0.9 }),
    );
    crystal.position.set((i - 1) * 1.15, 2.6 + i * 0.55, Math.sin(i) * 0.7);
    crystal.rotation.z = (i - 1) * 0.18;
    crystal.castShadow = true;
    group.add(crystal);
  }
  group.position.set(x, 0, z);
  group.scale.setScalar(scale);
  return group;
}

function makeRock(x: number, z: number): THREE.Group {
  const group = new THREE.Group();
  const rock = new THREE.Mesh(new THREE.DodecahedronGeometry(1.4 + Math.abs(Math.sin(x)) * 0.8, 0), new THREE.MeshLambertMaterial({ color: "#8d4932" }));
  rock.scale.y = 0.55;
  rock.position.y = 0.7;
  rock.castShadow = true;
  group.add(rock);
  group.position.set(x, 0, z);
  return group;
}

function createCropMesh(crop: CropState): THREE.Group {
  const group = new THREE.Group();
  group.userData.cropId = crop.id;
  group.position.set(crop.x, 0, crop.z);
  const base = new THREE.Mesh(new THREE.CylinderGeometry(1.8, 2.1, 0.22, 8), new THREE.MeshLambertMaterial({ color: "#693221" }));
  base.position.y = 0.12;
  base.scale.z = 0.72;
  base.receiveShadow = true;
  group.add(base);

  const target = new THREE.Mesh(new THREE.SphereGeometry(2.2, 10, 8), new THREE.MeshBasicMaterial({ visible: false }));
  target.userData.cropId = crop.id;
  target.position.y = 1.4;
  group.add(target);
  cropPickTargets.push(target);

  const growing = new THREE.Group();
  growing.name = "growing";
  for (let i = 0; i < 3; i += 1) {
    const sprout = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.72, 5), new THREE.MeshLambertMaterial({ color: "#57d96f" }));
    sprout.position.set((i - 1) * 0.45, 0.52, Math.sin(i) * 0.3);
    sprout.castShadow = true;
    growing.add(sprout);
  }
  group.add(growing);

  const ready = new THREE.Group();
  ready.name = "ready";
  group.add(ready);

  if (crop.type === "wheat") {
    for (let i = 0; i < 4; i += 1) {
      const stalk = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.08, 1.8, 5), new THREE.MeshLambertMaterial({ color: "#d8b441" }));
      stalk.position.set((i - 1.5) * 0.35, 0.9, Math.sin(i) * 0.28);
      stalk.castShadow = true;
      ready.add(stalk);
    }
  } else if (crop.type === "carrot") {
    const body = new THREE.Mesh(new THREE.ConeGeometry(0.45, 1.4, 8), new THREE.MeshLambertMaterial({ color: "#ef802d" }));
    body.position.y = 0.75;
    body.rotation.x = Math.PI;
    body.castShadow = true;
    ready.add(body);
    const top = new THREE.Mesh(new THREE.ConeGeometry(0.55, 1, 7), new THREE.MeshLambertMaterial({ color: "#2fa34f" }));
    top.position.y = 1.55;
    ready.add(top);
  } else if (crop.type === "pumpkin") {
    const pumpkin = new THREE.Mesh(new THREE.SphereGeometry(1.15, 12, 8), new THREE.MeshLambertMaterial({ color: "#ea7b23" }));
    pumpkin.position.y = 1.1;
    pumpkin.scale.set(1.25, 0.78, 1);
    pumpkin.castShadow = true;
    ready.add(pumpkin);
  } else if (crop.type === "apple") {
    const apple = new THREE.Mesh(new THREE.SphereGeometry(0.85, 12, 9), new THREE.MeshLambertMaterial({ color: "#da4d38" }));
    apple.position.y = 1.25;
    apple.castShadow = true;
    ready.add(apple);
  } else if (crop.type === "egg") {
    const egg = new THREE.Mesh(new THREE.SphereGeometry(0.75, 12, 9), new THREE.MeshLambertMaterial({ color: "#fff4d7" }));
    egg.position.y = 0.8;
    egg.scale.y = 1.22;
    egg.castShadow = true;
    ready.add(egg);
  } else if (crop.type === "milk") {
    const milk = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.55, 1.35, 9), new THREE.MeshLambertMaterial({ color: "#f8f8ec" }));
    milk.position.y = 0.85;
    milk.castShadow = true;
    ready.add(milk);
  } else {
    const wool = new THREE.Mesh(new THREE.SphereGeometry(0.9, 10, 8), new THREE.MeshLambertMaterial({ color: "#f2eee4" }));
    wool.position.y = 0.9;
    wool.scale.set(1.2, 0.8, 1);
    wool.castShadow = true;
    ready.add(wool);
  }

  setCropMeshState(group, crop.ready);
  return group;
}

function setCropMeshState(group: THREE.Group, ready: boolean): void {
  const readyGroup = group.getObjectByName("ready");
  const growingGroup = group.getObjectByName("growing");
  if (readyGroup) readyGroup.visible = ready;
  if (growingGroup) growingGroup.visible = !ready;
}

function createPlayerRig(player: PlayerState): PlayerRig {
  const group = new THREE.Group();
  const style = Object.values(CHARACTER_STYLES).find((item) => item.color === player.color && item.hat === player.hat) || CHARACTER_STYLES.farmer;
  const whiteMat = new THREE.MeshLambertMaterial({ color: "#fff7e7" });
  const bodyMat = new THREE.MeshLambertMaterial({ color: player.color });
  const darkMat = new THREE.MeshLambertMaterial({ color: "#14191b" });
  const highlightMat = new THREE.MeshLambertMaterial({ color: "#ffffff" });
  const mouthMat = new THREE.MeshLambertMaterial({ color: "#f37f63" });
  const accentMat = new THREE.MeshLambertMaterial({ color: style.accent });

  const lower = new THREE.Mesh(new THREE.CapsuleGeometry(0.66, 1.12, 8, 16), bodyMat);
  lower.position.y = 1.1;
  lower.scale.set(1.02, 1.05, 1.02);
  lower.castShadow = true;
  group.add(lower);

  const face = new THREE.Mesh(new THREE.SphereGeometry(0.68, 22, 16), whiteMat);
  face.position.y = 2.18;
  face.scale.set(1, 0.9, 0.96);
  face.castShadow = true;
  group.add(face);

  const faceBand = new THREE.Mesh(new THREE.CylinderGeometry(0.64, 0.66, 0.12, 20), whiteMat);
  faceBand.position.y = 1.72;
  faceBand.castShadow = true;
  group.add(faceBand);

  for (const side of [-1, 1]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.115, 12, 8), darkMat);
    eye.position.set(side * 0.24, 2.24, 0.58);
    eye.scale.set(0.82, 1.18, 0.24);
    group.add(eye);

    const shine = new THREE.Mesh(new THREE.SphereGeometry(0.034, 8, 6), highlightMat);
    shine.position.set(side * 0.21, 2.29, 0.66);
    shine.scale.set(1, 1, 0.18);
    group.add(shine);
  }

  const smile = new THREE.Mesh(new THREE.SphereGeometry(0.12, 12, 8), mouthMat);
  smile.position.set(0, 1.98, 0.62);
  smile.scale.set(1.45, 0.54, 0.2);
  group.add(smile);

  for (const side of [-1, 1]) {
    const foot = new THREE.Mesh(new THREE.CapsuleGeometry(0.13, 0.32, 5, 8), darkMat);
    foot.position.set(side * 0.32, 0.19, 0.26);
    foot.rotation.x = Math.PI / 2;
    foot.rotation.z = side * 0.14;
    foot.castShadow = true;
    group.add(foot);

    const arm = new THREE.Mesh(new THREE.CapsuleGeometry(0.1, 0.64, 5, 8), whiteMat);
    arm.position.set(side * 0.74, 1.42, 0.12);
    arm.rotation.z = side * -0.38;
    arm.castShadow = true;
    group.add(arm);

    const glove = new THREE.Mesh(new THREE.SphereGeometry(0.14, 10, 8), accentMat);
    glove.position.set(side * 0.88, 1.08, 0.16);
    glove.castShadow = true;
    group.add(glove);
  }

  const hatColor = player.hat === "yellow" ? "#f0c542" : player.hat === "green" ? "#1cb86f" : player.hat === "white" ? "#f7f2e8" : "#101418";
  const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.72, 0.76, 0.28, 16), new THREE.MeshLambertMaterial({ color: hatColor }));
  cap.position.y = 2.78;
  cap.castShadow = true;
  group.add(cap);
  const bill = new THREE.Mesh(new THREE.BoxGeometry(0.98, 0.12, 0.52), new THREE.MeshLambertMaterial({ color: hatColor }));
  bill.position.set(0, 2.7, 0.55);
  bill.castShadow = true;
  group.add(bill);

  const backpack = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.92, 0.3), new THREE.MeshLambertMaterial({ color: "#27322c" }));
  backpack.position.set(0, 1.3, -0.6);
  backpack.castShadow = true;
  group.add(backpack);

  if (style.accessory === "rake") {
    const handle = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 2.4, 6), new THREE.MeshLambertMaterial({ color: "#7b4a24" }));
    handle.position.set(-0.82, 1.45, 0.3);
    handle.rotation.z = 0.32;
    group.add(handle);
    const headTool = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.12, 0.16), new THREE.MeshLambertMaterial({ color: "#d7c189" }));
    headTool.position.set(-1.15, 0.48, 0.28);
    group.add(headTool);
  } else if (style.accessory === "candle") {
    const candle = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.13, 1.1, 8), new THREE.MeshLambertMaterial({ color: "#57d96f" }));
    candle.position.set(-0.9, 1.9, 0.18);
    group.add(candle);
    const flame = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.5, 7), new THREE.MeshLambertMaterial({ color: "#f0c542" }));
    flame.position.set(-0.9, 2.7, 0.18);
    group.add(flame);
  } else if (style.accessory === "hammer") {
    const handle = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 1.7, 6), new THREE.MeshLambertMaterial({ color: "#6e421e" }));
    handle.position.set(0.92, 1.38, 0.22);
    handle.rotation.z = -0.42;
    group.add(handle);
    const headTool = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.35, 0.35), new THREE.MeshLambertMaterial({ color: "#5a5b58" }));
    headTool.position.set(1.24, 1.96, 0.22);
    group.add(headTool);
  } else {
    const tablet = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.9, 0.08), new THREE.MeshLambertMaterial({ color: "#1a2222" }));
    tablet.position.set(0.86, 1.62, 0.48);
    tablet.rotation.x = -0.2;
    group.add(tablet);
    const screen = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.62, 0.04), new THREE.MeshLambertMaterial({ color: "#57d96f" }));
    screen.position.set(0.86, 1.62, 0.54);
    screen.rotation.x = -0.2;
    group.add(screen);
  }

  const label = makeTextSprite(player.name, 220, 52, "#ffffff", player.holder ? "#168f52" : "#29312f", 24);
  label.position.set(0, 3.55, 0);
  label.scale.set(3.8, 0.9, 1);
  group.add(label);

  group.position.set(player.x, player.y, player.z);
  group.rotation.y = player.rot;
  return { group, label, target: new THREE.Vector3(player.x, player.y, player.z), rot: player.rot };
}

function applyCharacterStyle(): void {
  const style = CHARACTER_STYLES[selectedCharacter];
  if (localPlayer) {
    localPlayer.color = style.color;
    localPlayer.hat = style.hat;
  }
  const replacement = createPlayerRig({
    ...(localPlayer || {
      id: "local",
      name: localName,
      x: localRig.group.position.x,
      y: localRig.group.position.y,
      z: localRig.group.position.z,
      rot: localRig.group.rotation.y,
      harvested: 0,
      cash: 0,
      wallet: "",
      holder: false,
    }),
    color: style.color,
    hat: style.hat,
  });
  while (localRig.group.children.length) {
    const child = localRig.group.children.pop();
    if (child) child.removeFromParent();
  }
  for (const child of [...replacement.group.children]) {
    localRig.group.add(child);
  }
  localRig.label = replacement.label;
}

async function connect(): Promise<void> {
  const endpointOverride = configuredGameEndpoint();
  if (!endpointOverride && (location.hostname.includes("netlify.app") || location.search.includes("offline=1"))) {
    startOfflineMode();
    return;
  }

  try {
    const protocol = location.protocol === "https:" ? "wss" : "ws";
    const gameHost = location.hostname;
    const gamePort = location.port;
    const endpoint = endpointOverride || (gamePort ? `${protocol}://${gameHost}:${gamePort}` : `${protocol}://${location.host}`);
    ui.connection.textContent = "joining";
    const client = new Client(endpoint);
    room = await withTimeout(client.joinOrCreate("farm"), 12_000);
    ui.connection.textContent = "online";

    room.onMessage("world:init", (payload: WorldInit) => {
      loadWorld(payload);
    });

    room.onMessage("player:join", addRemotePlayer);
    room.onMessage("player:update", (player: PlayerState) => {
      if (player.id === localId) {
        localPlayer = { ...player, name: localName };
      } else {
        updateRemotePlayer(player);
      }
      renderStats();
    });
    room.onMessage("player:leave", (id: string) => {
      const rig = remoteRigs.get(id);
      if (!rig) return;
      scene.remove(rig.group);
      remoteRigs.delete(id);
      renderStats();
    });
    room.onMessage("crop:update", (crop: CropState) => {
      crops = crops.map((item) => item.id === crop.id ? crop : item);
      const mesh = cropMeshes.get(crop.id);
      if (mesh) setCropMeshState(mesh, crop.ready);
      renderYield();
    });
    room.onMessage("inventory:update", (bag: Record<CropType, number>) => {
      inventory = bag;
      renderInventory();
      renderYield();
    });
    room.onMessage("orders:update", (next: OrderState[]) => {
      orders = next;
      renderOrders();
    });
    room.onMessage("leaderboard:update", (next: LeaderRow[]) => {
      leaders = next;
      renderLeaderboard();
    });
    room.onMessage("chat:new", (row: ChatRow) => {
      chatRows.unshift(row);
      chatRows.splice(70);
      renderChat();
    });
	    room.onMessage("toast", showToast);
	    room.onMessage("wallet:status", (payload: { wallet: string; holder: boolean; message: string }) => {
	      ui.walletLabel.textContent = payload.wallet ? `${payload.wallet.slice(0, 5)}...${payload.wallet.slice(-4)}` : "guest colonist";
	      if (localPlayer) localPlayer.holder = payload.holder;
	      renderHolderState();
	      renderLeaderboard();
	      showToast(payload.message);
	    });
	    room.onLeave((code) => {
	      if (code === 1000) return;
	      room = undefined;
	      ui.connection.textContent = "reconnecting";
	      showToast("Connection refreshed. Rejoining colony...");
	      window.setTimeout(() => location.reload(), 1600);
	    });
	    room.send("client:ready");
  } catch (error) {
    startOfflineMode();
    console.error(error);
  }
}

function configuredGameEndpoint(): string {
  const params = new URLSearchParams(location.search);
  const fromQuery = params.get("server")?.trim();
  const fromEnv = envValue("VITE_GAME_WS_URL");
  const isLocalHost = ["localhost", "127.0.0.1", "0.0.0.0"].includes(location.hostname) || location.hostname.endsWith(".localhost");
  const fromProduction = isLocalHost ? "" : PUBLIC_GAME_WS_URL;
  const endpoint = fromQuery || fromEnv || fromProduction || "";
  return endpoint.startsWith("ws://") || endpoint.startsWith("wss://") ? endpoint : "";
}

function configuredApiBase(): string {
  const params = new URLSearchParams(location.search);
  const fromQuery = params.get("api")?.trim();
  const fromEnv = envValue("VITE_GAME_API_URL");
  const wsEndpoint = configuredGameEndpoint();
  const derived = wsEndpoint
    ? wsEndpoint.replace(/^wss:\/\//, "https://").replace(/^ws:\/\//, "http://")
    : "";
  const isLocalHost = ["localhost", "127.0.0.1", "0.0.0.0"].includes(location.hostname) || location.hostname.endsWith(".localhost");
  const fromProduction = isLocalHost ? "" : PUBLIC_GAME_API_URL;
  const base = fromQuery || fromEnv || derived || fromProduction || location.origin;
  return base.replace(/\/$/, "");
}

function envValue(key: string): string {
  if (key === "VITE_GAME_WS_URL") return import.meta.env.VITE_GAME_WS_URL?.trim() || "";
  if (key === "VITE_GAME_API_URL") return import.meta.env.VITE_GAME_API_URL?.trim() || "";
  if (key === "VITE_TOKEN_MINT") return import.meta.env.VITE_TOKEN_MINT?.trim() || "";
  return "";
}

function loadWorld(payload: WorldInit): void {
  if (worldLoaded) return;
  worldLoaded = true;
  localId = payload.id;
  crops = payload.crops;
  orders = payload.orders;
  inventory = payload.inventory;
  chatRows = payload.chat;
  leaders = payload.leaderboard;
  for (const crop of crops) {
    const mesh = createCropMesh(crop);
    cropMeshes.set(crop.id, mesh);
    scene.add(mesh);
  }
  for (const player of payload.players) {
    if (player.id === localId) {
      localPlayer = { ...player, name: localName };
      localRig.group.position.set(player.x, player.y, player.z);
    } else {
      addRemotePlayer(player);
    }
  }
  renderAll();
  syncSavedHolderWallet();
}

function startOfflineMode(): void {
  if (worldLoaded) return;
  offlineMode = true;
  ui.connection.textContent = "preview mode";
  room = undefined;
  loadWorld(createOfflineWorld());
  showToast("Preview mode: multiplayer server not attached.");
}

function addRemotePlayer(player: PlayerState): void {
  if (player.id === localId || remoteRigs.has(player.id)) return;
  const rig = createPlayerRig(player);
  remoteRigs.set(player.id, rig);
  scene.add(rig.group);
  renderStats();
}

function updateRemotePlayer(player: PlayerState): void {
  let rig = remoteRigs.get(player.id);
  if (!rig) {
    addRemotePlayer(player);
    rig = remoteRigs.get(player.id);
  }
  if (!rig) return;
  rig.target.set(player.x, player.y, player.z);
  rig.rot = player.rot;
  rig.label.material = makeTextSprite(player.name, 256, 68, "#ffffff", player.holder ? "#168f52" : "#101418", 30).material;
}

function setLocalPlayerName(name: string): void {
  localName = cleanText(name, 18) || randomTrencherName();
  localStorage.setItem("marsFarmName", localName);
  if (localPlayer) localPlayer.name = localName;
  localRig.label.material = makeTextSprite(localName, 220, 52, "#ffffff", localPlayer?.holder ? "#168f52" : "#29312f", 24).material;
  renderLeaderboard();
  renderRewards();
}

function renderHolderState(): void {
  const payout = payoutAddress || holderWallet;
  if (holderWallet) {
    ui.walletLabel.textContent = `holder ${shortWallet(holderWallet)}`;
    ui.holderNotice.classList.add("verified");
    ui.holderNotice.textContent = `Holder wallet saved: ${shortWallet(holderWallet)}. Points can qualify for creator reward rounds; live SOL payouts route to ${payout ? shortWallet(payout) : "unset payout"} after live reward preflight is enabled.`;
  } else {
    ui.walletLabel.textContent = "holder pending";
    ui.holderNotice.classList.remove("verified");
    ui.holderNotice.textContent = "Play free. Verify holder wallet to qualify for creator reward rounds. SOL payouts go to the verified payout address after live reward preflight is enabled.";
  }
  renderRewards();
}

function wireInput(): void {
  window.addEventListener("resize", resize);
  resize();
  ui.playerName.value = localName;
  renderHolderState();

  document.querySelectorAll<HTMLButtonElement>(".character-option").forEach((button) => {
    const id = normalizeCharacter(button.dataset.character);
    button.classList.toggle("active", id === selectedCharacter);
    button.addEventListener("click", () => {
      playSound("ui");
      selectedCharacter = id;
      localStorage.setItem("marsFarmCharacter", selectedCharacter);
      document.querySelectorAll<HTMLButtonElement>(".character-option").forEach((item) => item.classList.toggle("active", item === button));
      applyCharacterStyle();
    });
  });

  ui.randomName.addEventListener("click", () => {
    playSound("ui");
    localName = randomTrencherName();
    ui.playerName.value = localName;
    setLocalPlayerName(localName);
  });

  ui.enterWorld.addEventListener("click", () => {
    enableAudioFromGesture();
    playSound("enter");
    const enteredName = cleanText(ui.playerName.value, 18);
    localName = enteredName || localName || randomTrencherName();
    ui.playerName.value = localName;
    localStorage.setItem("marsFarmName", localName);
    setLocalPlayerName(localName);
    gameEntered = true;
    ui.characterSelect.classList.add("hidden");
    canvas.focus();
    showToast(`${CHARACTER_STYLES[selectedCharacter].label} ready. Walk the colony.`);
  });

  window.addEventListener("keydown", (event) => {
    if (isTyping()) return;
    keys.add(event.code);
    if (event.code === "Space") {
      event.preventDefault();
      jump();
    }
    if (event.code === "KeyE") interact();
    if (event.code === "Enter") qs<HTMLInputElement>("#chatForm input").focus();
  });
  window.addEventListener("keyup", (event) => keys.delete(event.code));

  canvas.addEventListener("pointerdown", (event) => {
    if (!gameEntered) return;
    lookDragging = true;
    canvas.setPointerCapture(event.pointerId);
  });
  canvas.addEventListener("pointerup", (event) => {
    lookDragging = false;
    if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
  });
  canvas.addEventListener("pointermove", (event) => {
    if (!lookDragging) return;
    yaw -= event.movementX * 0.004;
    pitch = THREE.MathUtils.clamp(pitch + event.movementY * 0.003, -1.05, 0.82);
  });
  canvas.addEventListener("contextmenu", (event) => event.preventDefault());
  canvas.addEventListener("click", harvestFromClick);

  ui.chatForm.addEventListener("submit", (event) => {
    event.preventDefault();
    const input = ui.chatForm.elements.namedItem("message") as HTMLInputElement;
    const message = input.value.trim();
    if (!message) return;
    if (room) {
      room.send("chat:send", message);
    } else {
      chatRows.unshift({ id: `${Date.now()}`, author: localName, message, createdAt: new Date().toISOString() });
      renderChat();
    }
    input.value = "";
    input.blur();
  });

  ui.connectWallet.addEventListener("click", connectWallet);
  ui.verifyClose.addEventListener("click", closeVerifyModal);
  ui.verifyModal.addEventListener("click", (event) => {
    if (event.target === ui.verifyModal) closeVerifyModal();
  });
  ui.verifySubmit.addEventListener("click", submitHolderWallet);
  ui.holderRows.addEventListener("click", async (event) => {
    const row = (event.target as HTMLElement).closest<HTMLElement>("[data-wallet]");
    const wallet = row?.dataset.wallet;
    if (!wallet) return;
    await navigator.clipboard?.writeText(wallet).catch(() => undefined);
    showToast(`Copied holder ${shortWallet(wallet)}`);
  });
  wireMobileControls();
  ui.soundToggle.textContent = audioEnabled ? "Sound On" : "Sound Off";
  ui.soundToggle.addEventListener("click", () => {
    audioEnabled = !audioEnabled;
    localStorage.setItem("marsFarmAudio", audioEnabled ? "on" : "off");
    ui.soundToggle.textContent = audioEnabled ? "Sound On" : "Sound Off";
    if (audioEnabled) {
      enableAudioFromGesture();
      playSound("ui");
    } else {
      stopAudio();
    }
  });
  ui.setPayout.addEventListener("click", () => {
    playSound("ui");
    openVerifyModal();
  });
}

function wireMobileControls(): void {
  const joystick = ui.mobileControls.querySelector<HTMLElement>("[data-mobile-joystick]");
  const stick = ui.mobileControls.querySelector<HTMLElement>(".joystick-stick");
  let joystickPointerId: number | undefined;

  const resetJoystick = () => {
    mobileMove.set(0, 0);
    joystickPointerId = undefined;
    if (stick) stick.style.transform = "translate(-50%, -50%)";
  };

  const updateJoystick = (event: PointerEvent) => {
    if (!joystick || !stick) return;
    const rect = joystick.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const max = Math.max(24, Math.min(rect.width, rect.height) * 0.34);
    const rawX = event.clientX - centerX;
    const rawY = event.clientY - centerY;
    const length = Math.hypot(rawX, rawY);
    const scale = length > max ? max / length : 1;
    const knobX = rawX * scale;
    const knobY = rawY * scale;
    mobileMove.set(knobX / max, knobY / max);
    if (mobileMove.lengthSq() < 0.025) mobileMove.set(0, 0);
    stick.style.transform = `translate(-50%, -50%) translate(${knobX}px, ${knobY}px)`;
  };

  joystick?.addEventListener("pointerdown", (event) => {
    event.preventDefault();
    joystickPointerId = event.pointerId;
    joystick.setPointerCapture(event.pointerId);
    updateJoystick(event);
  });
  joystick?.addEventListener("pointermove", (event) => {
    if (event.pointerId !== joystickPointerId) return;
    event.preventDefault();
    updateJoystick(event);
  });
  joystick?.addEventListener("pointerup", (event) => {
    if (event.pointerId !== joystickPointerId) return;
    event.preventDefault();
    if (joystick.hasPointerCapture(event.pointerId)) joystick.releasePointerCapture(event.pointerId);
    resetJoystick();
  });
  joystick?.addEventListener("pointercancel", resetJoystick);

  ui.mobileControls.querySelectorAll<HTMLButtonElement>("[data-mobile-key]").forEach((button) => {
    const code = button.dataset.mobileKey;
    if (!code) return;
    const press = (event: PointerEvent | TouchEvent) => {
      event.preventDefault();
      keys.add(code);
      if (code === "Space") jump();
    };
    const release = (event: PointerEvent | TouchEvent) => {
      event.preventDefault();
      keys.delete(code);
    };
    button.addEventListener("pointerdown", press);
    button.addEventListener("pointerup", release);
    button.addEventListener("pointercancel", release);
    button.addEventListener("pointerleave", release);
  });
  ui.mobileControls.querySelectorAll<HTMLButtonElement>("[data-mobile-action='interact']").forEach((button) => {
    button.addEventListener("pointerdown", (event) => {
      event.preventDefault();
      interact();
    });
  });
}

function openVerifyModal(): void {
  ui.verifyWalletInput.value = holderWallet;
  ui.verifyPayoutInput.value = payoutAddress || holderWallet;
  ui.verifyModal.classList.remove("hidden");
  window.setTimeout(() => ui.verifyWalletInput.focus(), 30);
}

function closeVerifyModal(): void {
  ui.verifyModal.classList.add("hidden");
}

async function submitHolderWallet(): Promise<void> {
  const wallet = cleanText(ui.verifyWalletInput.value, 48);
  const payout = cleanText(ui.verifyPayoutInput.value, 48) || wallet;
  if (!wallet) {
    showToast("Paste a holder wallet first.");
    return;
  }
  holderWallet = wallet;
  payoutAddress = payout;
  localStorage.setItem("marsFarmHolderWallet", wallet);
  localStorage.setItem("marsFarmPayout", payout);
  closeVerifyModal();
  renderHolderState();
  if (room) {
    syncSavedHolderWallet(true);
  } else {
    showToast(`Holder saved. SOL payout route: ${shortWallet(payout)}`);
  }
}

function syncSavedHolderWallet(force = false): void {
  if (!room || !holderWallet || (!force && holderSyncSent)) return;
  holderSyncSent = true;
  room.send("wallet", { wallet: holderWallet, payout: payoutAddress || holderWallet });
}

function animate(): void {
  requestAnimationFrame(animate);
  const dt = Math.min(clock.getDelta(), 0.04);
  updateLocal(dt);
  updateRemotes(dt);
  if (SIMULATOR_ENABLED) updateDemoNpcs(dt);
  updateCamera(dt);
  updateInteractions();
  updateEffects(dt);
  updateRewardTimer();
  renderer.render(scene, camera);
}

function updateLocal(dt: number): void {
  if (!gameEntered) return;
  stepCooldown -= dt;
  const move = new THREE.Vector3();
  if (keys.has("KeyW") || keys.has("ArrowUp")) move.z -= 1;
  if (keys.has("KeyS") || keys.has("ArrowDown")) move.z += 1;
  if (keys.has("KeyA") || keys.has("ArrowLeft")) move.x -= 1;
  if (keys.has("KeyD") || keys.has("ArrowRight")) move.x += 1;
  const mobileMagnitude = Math.min(1, mobileMove.length());
  if (mobileMagnitude > 0.05) {
    move.x += mobileMove.x;
    move.z += mobileMove.y;
  }
  const desiredVelocity = new THREE.Vector3();
  if (move.lengthSq() > 0) {
    move.normalize();
    const forward = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
    const right = new THREE.Vector3(forward.z, 0, -forward.x);
    desiredVelocity
      .addScaledVector(forward, move.z)
      .addScaledVector(right, move.x)
      .normalize();
    const speedScale = mobileMagnitude > 0.05 ? Math.max(0.42, mobileMagnitude) : 1;
    desiredVelocity.multiplyScalar((keys.has("ShiftLeft") || keys.has("ShiftRight") ? SPRINT_SPEED : WALK_SPEED) * speedScale);
  }
  localVelocity.lerp(desiredVelocity, Math.min(1, dt * (desiredVelocity.lengthSq() > 0 ? 12 : 18)));
  const previous = localRig.group.position.clone();
  localRig.group.position.addScaledVector(localVelocity, dt);
  localRig.group.position.x = THREE.MathUtils.clamp(localRig.group.position.x, -WORLD_LIMIT, WORLD_LIMIT);
  localRig.group.position.z = THREE.MathUtils.clamp(localRig.group.position.z, -WORLD_LIMIT, WORLD_LIMIT);
  resolveCollisions(previous);
  if (localVelocity.lengthSq() > 0.06) {
    localRig.group.rotation.y = Math.atan2(localVelocity.x, localVelocity.z);
    if (grounded && stepCooldown <= 0) {
      playSound("step");
      stepCooldown = keys.has("ShiftLeft") || keys.has("ShiftRight") ? 0.23 : 0.34;
    }
  }

  wasGrounded = grounded;
  verticalVelocity -= GRAVITY * dt;
  localRig.group.position.y += verticalVelocity * dt;
  if (localRig.group.position.y <= PLAYER_HEIGHT) {
    localRig.group.position.y = PLAYER_HEIGHT;
    verticalVelocity = 0;
    if (!wasGrounded) playSound("land");
    grounded = true;
  }
  if (localPlayer) {
    localPlayer.x = localRig.group.position.x;
    localPlayer.y = localRig.group.position.y;
    localPlayer.z = localRig.group.position.z;
    localPlayer.rot = localRig.group.rotation.y;
  }
  const now = performance.now();
  if (room && localPlayer && now - lastSent > 60) {
    room.send("player:update", { ...localPlayer, name: localName });
    lastSent = now;
  }
}

function jump(): void {
  if (!gameEntered || !grounded) return;
  verticalVelocity = JUMP_SPEED;
  grounded = false;
  playSound("jump");
}

function resolveCollisions(previous: THREE.Vector3): void {
  for (const blocker of worldBlockers) {
    if (localRig.group.position.y > blocker.height) continue;
    const dx = localRig.group.position.x - blocker.x;
    const dz = localRig.group.position.z - blocker.z;
    const minDistance = blocker.radius + PLAYER_RADIUS;
    const distanceSq = dx * dx + dz * dz;
    if (distanceSq <= 0 || distanceSq >= minDistance * minDistance) continue;
    const distance = Math.sqrt(distanceSq);
    const push = (minDistance - distance) + 0.02;
    localRig.group.position.x += (dx / distance) * push;
    localRig.group.position.z += (dz / distance) * push;
    if (Math.abs(previous.x - localRig.group.position.x) > 0.001 || Math.abs(previous.z - localRig.group.position.z) > 0.001) {
      localVelocity.multiplyScalar(0.82);
    }
  }
  localRig.group.position.x = THREE.MathUtils.clamp(localRig.group.position.x, -WORLD_LIMIT, WORLD_LIMIT);
  localRig.group.position.z = THREE.MathUtils.clamp(localRig.group.position.z, -WORLD_LIMIT, WORLD_LIMIT);
}

function updateRemotes(dt: number): void {
  for (const rig of remoteRigs.values()) {
    rig.group.position.lerp(rig.target, Math.min(1, dt * 10));
    rig.group.rotation.y = THREE.MathUtils.lerp(rig.group.rotation.y, rig.rot, Math.min(1, dt * 9));
  }
}

function updateDemoNpcs(dt: number): void {
  for (const npc of demoNpcs) {
    const target = npc.path[npc.pathIndex];
    const pos = npc.rig.group.position;
    const dx = target.x - pos.x;
    const dz = target.z - pos.z;
    const distance = Math.hypot(dx, dz);
    if (distance < 2) {
      npc.pathIndex = (npc.pathIndex + 1) % npc.path.length;
      npc.speed = 16 + seeded(pos.x * 0.7 + pos.z, 0, 10);
    } else {
      const step = Math.min(distance, npc.speed * dt);
      pos.x += (dx / distance) * step;
      pos.z += (dz / distance) * step;
      npc.rig.group.rotation.y = Math.atan2(dx, dz);
    }
    npc.jumpTimer -= dt;
    if (npc.jumpTimer <= 0) {
      npc.jumpTimer = 1.2 + seeded(pos.x + pos.z, 0, 2.3);
    }
    const hop = npc.jumpTimer > 0.82 ? Math.sin((1.35 - npc.jumpTimer) * Math.PI * 3.4) * 0.95 : 0;
    pos.y = Math.max(0, hop);
    npc.taskTimer -= dt;
    if (npc.taskTimer <= 0) {
      npc.taskTimer = 3.4 + seeded(pos.x * 2.3 + pos.z, 0, 4.8);
      if (dist2(pos.x, pos.z, localRig.group.position.x, localRig.group.position.z) < 9000) {
        addNpcBurst(npc);
      }
    }
    npc.effectTimer -= dt;
    if (npc.effectTimer <= 0) {
      npc.effectTimer = 1.8 + seeded(pos.x + npc.pathIndex, 0, 2.2);
      if (dist2(pos.x, pos.z, localRig.group.position.x, localRig.group.position.z) < 6200) {
        spawnMiniParticles(pos.x, pos.z, npc.rig.group.children.length % 2 === 0 ? "#57d96f" : "#f0c542", 5);
      }
    }
  }
}

function addNpcBurst(npc: DemoNpc): void {
  const pos = npc.rig.group.position;
  const labels = ["harvest", "bonus", "task", "raid"];
  const label = labels[Math.floor(seeded(pos.x + pos.z + performance.now() * 0.001, 0, labels.length - 0.01))];
  spawnMiniParticles(pos.x, pos.z, label === "bonus" ? "#f0c542" : "#57d96f", 10);
  addWorldPopup(`${npc.name} ${label}`, pos.x, pos.z, label === "bonus" ? "#d77d27" : "#168f52");
  if (dist2(pos.x, pos.z, localRig.group.position.x, localRig.group.position.z) < 2200) playSound(label === "bonus" ? "bonus" : "npc");
}

function updateCamera(dt: number): void {
  const target = localRig.group.position;
  const distance = window.innerWidth < 760 ? 24 : 20;
  const height = window.innerWidth < 760 ? 15 : 12;
  const offset = new THREE.Vector3(
    Math.sin(yaw) * distance,
    height + pitch * 13,
    Math.cos(yaw) * distance,
  );
  const desired = target.clone().add(offset);
  desired.y = Math.max(target.y + 0.85, desired.y);
  camera.position.lerp(desired, Math.min(1, dt * 6));
  camera.lookAt(target.x, target.y + 2.3 + Math.max(0, -pitch) * 18, target.z);
}

function updateInteractions(): void {
  if (!gameEntered) {
    ui.actionHint.textContent = "Choose a colonist and enter the colony.";
    interactionPrompt.visible = false;
    yieldPrompts.forEach((prompt) => { prompt.visible = false; });
    return;
  }
  const pos = localRig.group.position;
  activeBonus = undefined;
  let nearestBonusDist = BONUS_INTERACT_DISTANCE_SQ;
  for (const bonus of bonuses) {
    if (!bonus.ready) continue;
    const distance = dist2(pos.x, pos.z, bonus.x, bonus.z);
    if (distance < nearestBonusDist) {
      nearestBonusDist = distance;
      activeBonus = bonus;
    }
  }
  activeCrop = undefined;
  let nearestCropDist = CROP_INTERACT_DISTANCE_SQ;
  for (const crop of crops) {
    if (!crop.ready) continue;
    const distance = dist2(pos.x, pos.z, crop.x, crop.z);
    if (distance < nearestCropDist) {
      nearestCropDist = distance;
      activeCrop = crop;
    }
  }
  nearOrderBoard = dist2(pos.x, pos.z, 90, 18) < 150;
  let promptPosition: THREE.Vector3 | undefined;
  if (activeBonus) {
    promptPosition = new THREE.Vector3(activeBonus.x, 3.7, activeBonus.z);
  } else if (activeCrop) {
    promptPosition = new THREE.Vector3(activeCrop.x, 3.4, activeCrop.z);
  } else if (nearOrderBoard) {
    promptPosition = new THREE.Vector3(90, 10.8, 18);
  }
  if (promptPosition) {
    interactionPrompt.position.copy(promptPosition);
    interactionPrompt.visible = true;
  } else {
    interactionPrompt.visible = false;
  }
  yieldPrompts.forEach((prompt) => {
    prompt.visible = dist2(pos.x, pos.z, prompt.position.x, prompt.position.z) < 1300;
  });
  const hint = activeBonus
    ? `E collect ${activeBonus.type} bonus`
    : activeCrop
    ? `E / click harvest ${activeCrop.type}`
    : nearOrderBoard
      ? "E complete available task"
      : window.innerWidth < 760
        ? "Hold joystick to move - drag screen to look - Jump / E interact"
        : "WASD move - Shift sprint - Space jump - Drag up to see Earth - E interact";
  ui.actionHint.textContent = hint;
}

function harvestNearest(): void {
  if (!activeCrop) {
    showToast("Move closer to a ready crop.");
    return;
  }
  sendLocalPositionNow();
  if (room) room.send("harvest", activeCrop.id);
  else harvestLocal(activeCrop.id);
}

function harvestFromClick(event: MouseEvent): void {
  if (isTyping()) return;
  mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
  mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
  raycaster.setFromCamera(mouse, camera);
  const hit = raycaster.intersectObjects(cropPickTargets, true)[0];
  const cropId = hit?.object.userData.cropId;
  if (cropId) {
    sendLocalPositionNow();
    if (room) room.send("harvest", cropId);
    else harvestLocal(cropId);
  }
}

function interact(): void {
  if (activeBonus) {
    collectBonus(activeBonus.id);
    return;
  }
  if (nearOrderBoard) {
    const order = orders.find((item) => canFillOrder(item));
    if (order && room) room.send("fill:order", order.id);
    else if (order) fillOrderLocal(order.id);
    else showToast("Need more goods for the order board.");
    return;
  }
  harvestNearest();
}

async function connectWallet(): Promise<void> {
  playSound("ui");
  openVerifyModal();
}

function sendLocalPositionNow(): void {
  if (!room || !localPlayer) return;
  localPlayer.x = localRig.group.position.x;
  localPlayer.y = localRig.group.position.y;
  localPlayer.z = localRig.group.position.z;
  localPlayer.rot = localRig.group.rotation.y;
  room.send("player:update", { ...localPlayer, name: localName });
  lastSent = performance.now();
}

function harvestLocal(cropId: string): void {
  const crop = crops.find((item) => item.id === cropId);
  if (!crop?.ready || !localPlayer) return;
  crop.ready = false;
  inventory[crop.type] += 1;
  localPlayer.harvested += 1;
  localPlayer.cash += cropValue(crop.type);
  const mesh = cropMeshes.get(crop.id);
  if (mesh) setCropMeshState(mesh, false);
  spawnHarvestParticles(crop.x, crop.z, crop.type);
  addWorldPopup(`+${cropValue(crop.type)} pts`, crop.x, crop.z, "#138b48");
  playSound("harvest");
  renderAll();
  showToast(`${crop.type} harvested +${cropValue(crop.type)} points`);
  window.setTimeout(() => {
    crop.ready = true;
    const cropMesh = cropMeshes.get(crop.id);
    if (cropMesh) setCropMeshState(cropMesh, true);
    renderYield();
  }, 24000);
}

function fillOrderLocal(orderId: string): void {
  const order = orders.find((item) => item.id === orderId);
  if (!order || !localPlayer || !canFillOrder(order)) return;
  for (const [type, amount] of Object.entries(order.wants)) {
    inventory[type as CropType] -= Number(amount || 0);
  }
  localPlayer.cash += order.reward;
  orders = orders.filter((item) => item.id !== orderId);
  orders.push(createOfflineOrder(`${Date.now()}`));
  leaders = [{ id: "preview", name: localName, cash: localPlayer.cash, harvested: localPlayer.harvested, wallet: "", holder: false }];
  chatRows.unshift({
    id: `${Date.now()}`,
    author: "Mission Board",
    message: `${localName} completed a task for ${order.reward} points.`,
    createdAt: new Date().toISOString(),
  });
  renderAll();
  addWorldPopup(`+${order.reward} pts`, localRig.group.position.x, localRig.group.position.z, "#d77d27");
  playSound("task");
  showToast(`Task complete +${order.reward} points`);
}

function collectBonus(id: string): void {
  const bonus = bonuses.find((item) => item.id === id);
  if (!bonus?.ready || !localPlayer) return;
  bonus.ready = false;
  localPlayer.cash += bonus.value;
  localPlayer.harvested += bonus.type === "crate" ? 2 : 0;
  const mesh = bonusMeshes.get(bonus.id);
  if (mesh) mesh.visible = false;
  addWorldPopup(`+${bonus.value} pts`, bonus.x, bonus.z, bonus.type === "boost" ? "#168f52" : "#d77d27");
  playSound("bonus");
  showToast(`${bonus.type} bonus +${bonus.value} points`);
  renderAll();
  window.setTimeout(() => {
    bonus.ready = true;
    const bonusMesh = bonusMeshes.get(bonus.id);
    if (bonusMesh) bonusMesh.visible = true;
  }, bonus.type === "crate" ? 16000 : 11000);
}

function spawnMiniParticles(x: number, z: number, color: string, count: number): void {
  for (let i = 0; i < count; i += 1) {
    const mesh = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.18 + (i % 2) * 0.08, 0),
      new THREE.MeshLambertMaterial({ color }),
    );
    const angle = seeded(x + z + i * 12.7, 0, Math.PI * 2);
    const speed = 2.1 + seeded(x * 0.2 + i, 0, 2.8);
    mesh.position.set(x, 1.3 + seeded(z + i, 0, 1), z);
    mesh.castShadow = true;
    scene.add(mesh);
    harvestParticles.push({
      mesh,
      life: 0.55 + seeded(i + x, 0, 0.45),
      velocity: new THREE.Vector3(Math.cos(angle) * speed, 3.3 + seeded(i + z, 0, 2), Math.sin(angle) * speed),
    });
  }
}

function spawnHarvestParticles(x: number, z: number, type: CropType): void {
  const colors: Record<CropType, string> = {
    wheat: "#d8b441",
    carrot: "#ef802d",
    pumpkin: "#ea7b23",
    apple: "#da4d38",
    egg: "#fff4d7",
    milk: "#f8f8ec",
    wool: "#f2eee4",
  };
  for (let i = 0; i < 12; i += 1) {
    const mesh = new THREE.Mesh(
      new THREE.DodecahedronGeometry(0.22 + (i % 3) * 0.05, 0),
      new THREE.MeshLambertMaterial({ color: colors[type] }),
    );
    const angle = (i / 12) * Math.PI * 2;
    const speed = 3.3 + (i % 4) * 0.75;
    mesh.position.set(x, 1.5 + (i % 3) * 0.15, z);
    mesh.castShadow = true;
    scene.add(mesh);
    harvestParticles.push({
      mesh,
      life: 0.85 + (i % 4) * 0.08,
      velocity: new THREE.Vector3(Math.cos(angle) * speed, 4.2 + (i % 5) * 0.42, Math.sin(angle) * speed),
    });
  }
}

function addWorldPopup(text: string, x: number, z: number, color: string): void {
  const sprite = makeTextSprite(text, 190, 58, "#fff8e7", color, 26);
  sprite.position.set(x, 4.4, z);
  sprite.scale.set(4.4, 1.2, 1);
  scene.add(sprite);
  popupTexts.push({ sprite, life: 1.45, velocity: new THREE.Vector3(0, 2.1, 0) });
}

function updateEffects(dt: number): void {
  for (let i = popupTexts.length - 1; i >= 0; i -= 1) {
    const popup = popupTexts[i];
    popup.life -= dt;
    popup.sprite.position.addScaledVector(popup.velocity, dt);
    popup.sprite.scale.multiplyScalar(1 + dt * 0.12);
    const material = popup.sprite.material as THREE.SpriteMaterial;
    material.opacity = THREE.MathUtils.clamp(popup.life / 1.45, 0, 1);
    if (popup.life <= 0) {
      scene.remove(popup.sprite);
      material.map?.dispose();
      material.dispose();
      popupTexts.splice(i, 1);
    }
  }

  for (let i = harvestParticles.length - 1; i >= 0; i -= 1) {
    const particle = harvestParticles[i];
    particle.life -= dt;
    particle.velocity.y -= 12 * dt;
    particle.mesh.position.addScaledVector(particle.velocity, dt);
    particle.mesh.rotation.x += dt * 7;
    particle.mesh.rotation.y += dt * 5;
    particle.mesh.scale.setScalar(Math.max(0.05, particle.life));
    if (particle.life <= 0) {
      scene.remove(particle.mesh);
      particle.mesh.geometry.dispose();
      const material = particle.mesh.material;
      if (Array.isArray(material)) material.forEach((item) => item.dispose());
      else material.dispose();
      harvestParticles.splice(i, 1);
    }
  }

  const bob = Math.sin(performance.now() * 0.004) * 0.08;
  if (interactionPrompt.visible) interactionPrompt.position.y += bob * dt * 8;
  for (const prompt of yieldPrompts) {
    if (prompt.visible) prompt.position.y += bob * dt * 6;
  }
  for (const [id, mesh] of bonusMeshes) {
    if (!mesh.visible) continue;
    const bonus = bonuses.find((item) => item.id === id);
    const offset = bonus ? bonus.x * 0.01 + bonus.z * 0.01 : 0;
    mesh.rotation.y += dt * 1.7;
    mesh.position.y = Math.sin(performance.now() * 0.003 + offset) * 0.2;
  }
}

function enableAudioFromGesture(): void {
  if (!audioEnabled) return;
  const kit = ensureAudio();
  if (!kit) return;
  kit.context.resume().catch(() => {});
  startMusic();
  ui.soundToggle.textContent = "Sound On";
}

function ensureAudio(): AudioKit | undefined {
  if (audioKit) return audioKit;
  const AudioCtor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtor) return undefined;
  const context = new AudioCtor();
  const master = context.createGain();
  const music = context.createGain();
  const sfx = context.createGain();
  const ambience = context.createGain();
  master.gain.value = 0.82;
  music.gain.value = 0.18;
  sfx.gain.value = 0.2;
  ambience.gain.value = 0;
  music.connect(master);
  sfx.connect(master);
  ambience.connect(master);
  master.connect(context.destination);
  audioKit = { context, master, music, sfx, ambience };
  return audioKit;
}

function startMusic(): void {
  const kit = ensureAudio();
  if (!kit || kit.musicTimer) return;
  let step = 0;
  const melody = [329.63, 392, 493.88, 587.33, 493.88, 392, 369.99, 440, 523.25, 659.25, 523.25, 440];
  const harmony = [164.81, 196, 246.94, 220];
  kit.musicTimer = window.setInterval(() => {
    if (!audioEnabled || kit.context.state === "closed") return;
    const now = kit.context.currentTime;
    playTone(melody[step % melody.length], 0.32, "sine", 0.045, kit.music, now);
    if (step % 4 === 0) {
      const root = harmony[(step / 4) % harmony.length];
      playTone(root, 2.4, "sine", 0.024, kit.music, now + 0.02);
      playTone(root * 1.5, 2.2, "triangle", 0.014, kit.music, now + 0.02);
    }
    step = (step + 1) % 48;
  }, 540);
}

function stopAudio(): void {
  if (!audioKit) return;
  if (audioKit.musicTimer) {
    window.clearInterval(audioKit.musicTimer);
    audioKit.musicTimer = undefined;
  }
  audioKit.ambienceSource?.stop();
  audioKit.ambienceSource = undefined;
}

function playSound(kind: string): void {
  if (!audioEnabled) return;
  const kit = ensureAudio();
  if (!kit) return;
  kit.context.resume().catch(() => {});
  const now = kit.context.currentTime;
  if (kind === "ui") {
    playTone(620, 0.05, "triangle", 0.08, kit.sfx, now);
  } else if (kind === "enter") {
    [392, 523.25, 659.25].forEach((freq, index) => playTone(freq, 0.16, "triangle", 0.12, kit.sfx, now + index * 0.08));
  } else if (kind === "step") {
    playNoise(0.018, 0.012, 260);
    playTone(120, 0.026, "sine", 0.014, kit.sfx, now);
  } else if (kind === "jump") {
    playSweep(220, 520, 0.18, 0.12);
  } else if (kind === "land") {
    playNoise(0.04, 0.03, 180);
  } else if (kind === "harvest") {
    playNoise(0.05, 0.04, 1200);
    playTone(523.25, 0.09, "triangle", 0.08, kit.sfx, now + 0.03);
  } else if (kind === "bonus") {
    [659.25, 783.99, 987.77].forEach((freq, index) => playTone(freq, 0.12, "sine", 0.12, kit.sfx, now + index * 0.05));
  } else if (kind === "task") {
    [329.63, 392, 493.88, 659.25].forEach((freq, index) => playTone(freq, 0.12, "triangle", 0.1, kit.sfx, now + index * 0.06));
  } else if (kind === "npc") {
    playTone(460 + Math.random() * 160, 0.06, "triangle", 0.022, kit.sfx, now);
  }
}

function playTone(freq: number, duration: number, type: OscillatorType, volume: number, destination: AudioNode, when: number): void {
  const kit = ensureAudio();
  if (!kit) return;
  const osc = kit.context.createOscillator();
  const gain = kit.context.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, when);
  gain.gain.setValueAtTime(0.0001, when);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, volume), when + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, when + duration);
  osc.connect(gain);
  gain.connect(destination);
  osc.start(when);
  osc.stop(when + duration + 0.03);
}

function playSweep(from: number, to: number, duration: number, volume: number): void {
  const kit = ensureAudio();
  if (!kit) return;
  const now = kit.context.currentTime;
  const osc = kit.context.createOscillator();
  const gain = kit.context.createGain();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(from, now);
  osc.frequency.exponentialRampToValueAtTime(to, now + duration);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(volume, now + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  osc.connect(gain);
  gain.connect(kit.sfx);
  osc.start(now);
  osc.stop(now + duration + 0.03);
}

function playNoise(duration: number, volume: number, filterFreq: number): void {
  const kit = ensureAudio();
  if (!kit) return;
  const now = kit.context.currentTime;
  const buffer = kit.context.createBuffer(1, Math.max(1, Math.floor(kit.context.sampleRate * duration)), kit.context.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
  const source = kit.context.createBufferSource();
  const filter = kit.context.createBiquadFilter();
  const gain = kit.context.createGain();
  filter.type = "lowpass";
  filter.frequency.value = filterFreq;
  gain.gain.setValueAtTime(volume, now);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  source.buffer = buffer;
  source.connect(filter);
  filter.connect(gain);
  gain.connect(kit.sfx);
  source.start(now);
  source.stop(now + duration + 0.02);
}

function renderAll(): void {
  renderStats();
  renderYield();
  renderInventory();
  renderOrders();
  renderRewards();
  renderTokenSummary();
  renderChat();
  renderLeaderboard();
}

function renderStats(): void {
  ui.harvested.textContent = String(localPlayer?.harvested || 0);
  const points = localPlayer?.cash || 0;
  ui.cash.textContent = Math.round(points).toLocaleString();
  const visibleOthers = remoteRigs.size + (SIMULATOR_ENABLED ? demoNpcs.length : 0);
  ui.online.textContent = String((offlineMode ? Math.max(visibleOthers, demoNpcs.length) : visibleOthers) + 1);
  const progress = Math.min(1, (localPlayer?.harvested || 0) / 15);
  ui.questFill.style.width = `${progress * 100}%`;
  const colonyLevel = Math.max(1, Math.min(12, Math.floor(points / 280) + 1));
  if (points > 0) {
    ui.questTitle.textContent = `Colony Level ${colonyLevel}`;
    ui.questText.textContent = colonyLevel < 4
      ? "Harvest crops, find water, and clear mission board tasks."
      : "Run outer fields, collect bonuses, and defend your reward share.";
  }
  if (progress >= 1) {
    ui.questTitle.textContent = `Colony Level ${colonyLevel}`;
    ui.questText.textContent = "Complete missions, collect water finds, and climb the points leaderboard.";
  }
}

function renderYield(): void {
  const rows: [CropType, string][] = [
    ["wheat", "Hydro wheat"],
    ["carrot", "Red tubers"],
    ["pumpkin", "Mars pumpkins"],
    ["apple", "Dome fruit"],
    ["egg", "Oxygen pods"],
    ["milk", "Ice canisters"],
    ["wool", "Solar fiber"],
  ];
  ui.yieldList.innerHTML = rows.map(([type, label]) => {
    const ready = crops.filter((crop) => crop.type === type && crop.ready).length;
    return `<div><span>${label}</span><b>${ready}</b></div>`;
  }).join("");
}

function renderInventory(): void {
  const labels: Record<CropType, string> = {
    wheat: "WHT",
    carrot: "CRT",
    pumpkin: "PMP",
    apple: "APL",
    egg: "EGG",
    milk: "MLK",
    wool: "WOL",
  };
  ui.inventory.innerHTML = Object.entries(labels)
    .map(([type, label]) => `<div><span>${label}</span><b>${inventory[type as CropType] || 0}</b></div>`)
    .join("");
}

function renderOrders(): void {
  ui.orders.innerHTML = orders.map((order) => {
    const wants = Object.entries(order.wants)
      .map(([type, amount]) => {
        const have = inventory[type as CropType] || 0;
        return `<span class="${have >= Number(amount) ? "ok" : ""}">${type} ${have}/${amount}</span>`;
      })
      .join("");
    return `<article>
      <header><strong>${escapeHtml(order.customer)}</strong><b>+${order.reward} pts</b></header>
      <div>${wants}</div>
      <button type="button" data-order="${order.id}" ${canFillOrder(order) ? "" : "disabled"}>Complete Task</button>
    </article>`;
  }).join("");
  ui.orders.querySelectorAll<HTMLButtonElement>("button[data-order]").forEach((button) => {
    button.addEventListener("click", () => {
      const orderId = button.dataset.order;
      if (!orderId) return;
      if (room) room.send("fill:order", orderId);
      else fillOrderLocal(orderId);
    });
  });
}

function updateRewardTimer(): void {
  const now = Date.now();
  if (now >= rewardRoundEndsAt) {
    rewardRoundEndsAt = now + REWARD_ROUND_SECONDS * 1000;
    showToast("New creator reward round started. Keep farming points.");
  }
  const seconds = Math.max(0, Math.ceil((rewardRoundEndsAt - now) / 1000));
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  const text = `${minutes}:${String(remainder).padStart(2, "0")}`;
  if (text !== lastRewardTimerValue) {
    lastRewardTimerValue = text;
    ui.rewardTimer.textContent = text;
  }
}

function renderRewards(): void {
  const eligible = Boolean(holderWallet);
  ui.rewardPot.textContent = "0.000 SOL";
  ui.rewardShare.textContent = eligible ? "pending" : "Verify";
  ui.rewardReserve.textContent = "0.000 SOL";
  const rows = [
    { name: "Live reward round", value: "pre-launch", wallet: "starts after token creator fees are claimable" },
    { name: "Player pool", value: "70%", wallet: "distributed only from claimed creator fees" },
    { name: "Game reserve", value: "30%", wallet: "retained for development" },
    { name: localName, value: eligible ? "verified" : "not verified", wallet: eligible ? `payout ${shortWallet(payoutAddress || holderWallet)}` : "verify holder after launch" },
  ];
  ui.rewardRows.innerHTML = rows
    .map((row) => `<div><span>${escapeHtml(row.name)}<small>${escapeHtml(row.wallet)}</small></span><b>${escapeHtml(row.value)}</b></div>`)
    .join("");
}

async function refreshTokenSummary(): Promise<void> {
  ui.tokenMint.textContent = TOKEN_MINT;
  const apiBase = configuredApiBase();
  try {
    const response = await fetch(`${apiBase}/api/token-summary?mint=${encodeURIComponent(TOKEN_MINT)}`, { cache: "no-store" });
    if (!response.ok) throw new Error(`api_${response.status}`);
    latestTokenSummary = await response.json() as TokenSummary;
    renderTokenSummary();
  } catch (error) {
    latestTokenSummary = undefined;
    ui.tokenStats.innerHTML = `
      <div><span>Backend</span><b>offline</b></div>
      <div><span>Claims</span><b>dry-run</b></div>
      <div><span>Asset</span><b>SOL</b></div>
      <div><span>CA</span><b>${escapeHtml(shortWallet(TOKEN_MINT))}</b></div>
      <div><span>Status</span><b>${escapeHtml(error instanceof Error ? error.message : "unavailable")}</b></div>
    `;
    ui.holderRows.innerHTML = `<div><b>--</b><code>Connect game API</code><strong>waiting</strong></div>`;
  }
}

function renderTokenSummary(): void {
  const summary = latestTokenSummary;
  if (!summary) return;
  const volume = summary.recentVolumeUsd > 0 ? `$${summary.recentVolumeUsd.toFixed(0)}` : "$0";
  ui.tokenStats.innerHTML = `
    <div><span>Backend</span><b>${summary.ok ? "live" : "dry-run"}</b></div>
    <div><span>Claims</span><b>${summary.rewardEngine.realClaims ? "live" : "off"}</b></div>
    <div><span>Asset</span><b>${escapeHtml(summary.rewardEngine.payoutAsset || "SOL")}</b></div>
    <div><span>Rows</span><b>${summary.holderRowsSeen}</b></div>
    <div><span>Eligible</span><b>${summary.eligibleHolders}</b></div>
    <div><span>Trades</span><b>${summary.recentTrades}</b></div>
    <div><span>Volume</span><b>${volume}</b></div>
  `;
  const rows = summary.topHolders.length ? summary.topHolders : [];
  ui.holderRows.innerHTML = rows.map((row) => {
    const qty = row.quantity > 0 ? compactNumber(row.quantity) : row.isLiqPool ? "LP" : "0";
    return `<div data-wallet="${escapeHtml(row.account)}" title="Click to copy ${escapeHtml(row.account)}">
      <b>${row.rank}</b>
      <code title="${escapeHtml(row.account)}">${escapeHtml(row.accountShort || shortWallet(row.account))}</code>
      <strong>${escapeHtml(qty)}</strong>
    </div>`;
  }).join("") || `<div><b>--</b><code>No holder rows yet</code><strong>0</strong></div>`;
}

function renderChat(): void {
  ui.chatLog.innerHTML = chatRows.slice(0, 28).map((row) => `
    <div>
      <strong>${escapeHtml(row.author)}</strong>
      <span>${escapeHtml(row.message)}</span>
    </div>
  `).join("");
}

function renderLeaderboard(): void {
  const previewLeaders = offlineMode
    ? [
      { id: "local", name: localName, cash: localPlayer?.cash || 0, harvested: localPlayer?.harvested || 0, wallet: holderWallet ? shortWallet(payoutAddress || holderWallet) : "unverified", holder: Boolean(holderWallet) },
      ...TRENCHER_NAMES.slice(0, 8).map((name, index) => ({
        id: `demo-leader-${index}`,
        name,
        cash: 620 - index * 44,
        harvested: 24 - index,
        wallet: index % 2 === 0 ? "verified" : "",
        holder: index % 2 === 0,
      })),
    ].sort((a, b) => b.cash - a.cash)
    : leaders;
  ui.leaderboard.innerHTML = previewLeaders.slice(0, 24).map((row, index) => `
    <div>
      <b>${index + 1}</b>
      <span>${escapeHtml(row.name)}${row.holder ? " - holder" : ""}${row.id === "local" ? ` - ${escapeHtml(row.wallet)}` : ""}</span>
      <strong>${Math.round(row.cash).toLocaleString()} pts</strong>
    </div>
  `).join("");
}

function canFillOrder(order: OrderState): boolean {
  return Object.entries(order.wants).every(([type, amount]) => (inventory[type as CropType] || 0) >= Number(amount || 0));
}

function makeTextSprite(text: string, width: number, height: number, color: string, bg: string, size: number): THREE.Sprite {
  const canvas2d = document.createElement("canvas");
  canvas2d.width = width;
  canvas2d.height = height;
  const ctx = canvas2d.getContext("2d")!;
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = "#101418";
  ctx.lineWidth = 8;
  ctx.strokeRect(4, 4, width - 8, height - 8);
  ctx.fillStyle = color;
  ctx.font = `900 ${size}px ui-monospace, Menlo, Consolas, monospace`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text.slice(0, 22), width / 2, height / 2 + 1);
  const texture = new THREE.CanvasTexture(canvas2d);
  texture.minFilter = THREE.LinearFilter;
  const material = new THREE.SpriteMaterial({ map: texture, depthWrite: false, transparent: true });
  return new THREE.Sprite(material);
}

function makeMountedSign(text: string, width: number, height: number, color: string, bg: string, size: number, scaleX: number, scaleY: number): SignHandle {
  const group = new THREE.Group();
  const backing = new THREE.Mesh(
    new THREE.BoxGeometry(scaleX + 0.45, scaleY + 0.32, 0.36),
    new THREE.MeshLambertMaterial({ color: "#5a3826" }),
  );
  backing.castShadow = true;
  backing.receiveShadow = true;
  group.add(backing);

  const canvas2d = document.createElement("canvas");
  canvas2d.width = width;
  canvas2d.height = height;
  const ctx = canvas2d.getContext("2d")!;
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = "#101418";
  ctx.lineWidth = 8;
  ctx.strokeRect(4, 4, width - 8, height - 8);
  ctx.fillStyle = color;
  ctx.font = `900 ${size}px ui-monospace, Menlo, Consolas, monospace`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text.slice(0, 24), width / 2, height / 2 + 1);

  const texture = new THREE.CanvasTexture(canvas2d);
  texture.minFilter = THREE.LinearFilter;
  texture.colorSpace = THREE.SRGBColorSpace;
  const face = new THREE.Mesh(
    new THREE.PlaneGeometry(scaleX, scaleY),
    new THREE.MeshBasicMaterial({ map: texture }),
  );
  face.position.z = 0.22;
  group.add(face);
  return { group, face };
}

function resize(): void {
  const width = window.innerWidth;
  const height = window.innerHeight;
  camera.aspect = width / height;
  camera.updateProjectionMatrix();
  renderer.setSize(width, height, false);
}

function showToast(message: string): void {
  ui.toast.textContent = message;
  ui.toast.classList.add("show");
  window.clearTimeout(Number(ui.toast.dataset.timer || 0));
  const timer = window.setTimeout(() => ui.toast.classList.remove("show"), 2400);
  ui.toast.dataset.timer = String(timer);
}

function emptyInventory(): Record<CropType, number> {
  return { wheat: 0, carrot: 0, pumpkin: 0, apple: 0, egg: 0, milk: 0, wool: 0 };
}

function randomTrencherName(): string {
  return TRENCHER_NAMES[Math.floor(Math.random() * TRENCHER_NAMES.length)];
}

function cleanText(value: unknown, maxLength: number): string {
  return String(value ?? "")
    .replace(/[<>]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLength);
}

function shortWallet(value: string): string {
  return value.length > 12 ? `${value.slice(0, 5)}...${value.slice(-4)}` : value;
}

function compactNumber(value: number): string {
  if (!Number.isFinite(value)) return "0";
  if (Math.abs(value) >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toFixed(value >= 1 ? 2 : 4);
}

function normalizeCharacter(value: unknown): CharacterId {
  return value === "degen" || value === "builder" || value === "trader" || value === "farmer" ? value : "farmer";
}

function createOfflineWorld(): WorldInit {
  const style = CHARACTER_STYLES[selectedCharacter];
  const previewPlayer: PlayerState = {
    id: "preview",
    name: localName,
    x: -8,
    y: 0,
    z: 6,
    rot: 0,
    color: style.color,
    hat: style.hat,
    harvested: 0,
    cash: 0,
    wallet: "",
    holder: false,
  };
  const previewCrops: CropState[] = [];
  for (const [rowIndex, row] of CROP_LAYOUT.entries()) {
    const spacing = row.spacing || 5.5;
    for (let i = 0; i < row.count; i += 1) {
      previewCrops.push({
        id: `${row.type}-${rowIndex}-${i}`,
        type: row.type,
        x: row.x0 + i * spacing,
        z: row.z + Math.sin(i) * 1.4,
        ready: true,
      });
    }
  }
  const previewOrders = ["preview-a", "preview-b", "preview-c"].map(createOfflineOrder);
  return {
    id: "preview",
    players: [previewPlayer],
    crops: previewCrops,
    orders: previewOrders,
    chat: [
      {
        id: "preview-chat",
        author: "COLONY",
        message: "Preview mode is local. The multiplayer server is not attached.",
        createdAt: new Date().toISOString(),
      },
    ],
    inventory: emptyInventory(),
    leaderboard: [{ id: "preview", name: localName, cash: 0, harvested: 0, wallet: "", holder: false }],
  };
}

function createOfflineOrder(id: string): OrderState {
  const customers = ["Oxygen Run", "Ice Vault Restock", "Meme Lab", "Dome Shift", "Crater Raid", "Holder Mission"];
  const cropTypes: CropType[] = ["wheat", "carrot", "pumpkin", "apple", "egg", "milk", "wool"];
  const first = cropTypes[Math.floor(Math.random() * cropTypes.length)];
  const second = cropTypes[Math.floor(Math.random() * cropTypes.length)];
  const wants: Partial<Record<CropType, number>> = {
    [first]: 1 + Math.floor(Math.random() * 3),
    [second]: 1 + Math.floor(Math.random() * 2),
  };
  const total = Object.entries(wants).reduce((sum, [type, amount]) => sum + cropValue(type as CropType) * Number(amount || 0), 0);
  return {
    id,
    customer: customers[Math.floor(Math.random() * customers.length)],
    reward: Math.round(total * 3.2 + 80),
    wants,
  };
}

function cropValue(type: CropType): number {
  return {
    wheat: 5,
    carrot: 8,
    pumpkin: 18,
    apple: 10,
    egg: 14,
    milk: 16,
    wool: 20,
  }[type];
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error("connection timeout")), ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });
}

function dist2(ax: number, az: number, bx: number, bz: number): number {
  return (ax - bx) ** 2 + (az - bz) ** 2;
}

function seeded(seed: number, min: number, max: number): number {
  const n = Math.sin(seed) * 10000;
  return min + (n - Math.floor(n)) * (max - min);
}

function isTyping(): boolean {
  const active = document.activeElement;
  return Boolean(active && ["INPUT", "TEXTAREA", "SELECT"].includes(active.tagName));
}

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function qs<T extends HTMLElement = HTMLElement>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing selector: ${selector}`);
  return element;
}

declare global {
  interface Window {
    solana?: { connect: () => Promise<{ publicKey?: { toString: () => string } }> };
    solflare?: { connect: () => Promise<{ publicKey?: { toString: () => string } }> };
  }
}
